import { defineChatTool } from "@codenexus/react";
import type { ChatTool } from "@codenexus/react";
import { listUserProjectsApi, fetchProjectFeaturesApi } from "@/api/api";
import { fetchIntegrationLinks } from "@/api/integrations/links";
import { getUserStoriesByProject } from "@/api/userStoryApi";
import fetchWithAuth from "@/api/secureFetch";
import { listContextMeshes, listContextMeshSources, resolveContextMeshByName } from "../contextMesh/contextMeshApi";
import type {
    AppChatEventMap,
    RequestWorkProductGeneration,
    WorkProductConfirmationCandidate,
    WorkProductGenerationResult
} from "./types";
import type { FeatureScope, NewProductDraftPatch, NewProductDraftSnapshot, PrimaryFormFactor, ProductKind } from "../newProduct/state";
import { uploadFileToKnowledgeBaseTool } from "./uploadFileToKnowledgeBase";
import { createJiraConnectTools } from "./jiraConnect/tools";
import {
    createIntegrationDisconnectTool,
    type DisconnectStateStore
} from "./integrationDisconnect/integrationDisconnect";

// User-story statuses we consider "closed" — everything else is "open".
const CLOSED_STATUSES = new Set(["completed", "accepted", "archived", "cancelled", "done"]);

// Max stories returned to the LLM per call. Keeps the context lean; users
// can ask for more specific filters if truncated.
const MAX_STORIES = 50;
const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

interface ProjectRecord {
    project_id: number;
    project_name?: string;
    status?: string;
    project_description?: string | null;
    last_updated?: string | null;
}

interface FeatureRecord {
    id?: number | string;
    featureid?: number | string;
    title?: string;
    name?: string;
    description?: string | null;
}

interface UserStoryRecord {
    id: string | number;
    story_number?: string;
    title?: string;
    feature_id?: number | string;
    status?: string;
    priority?: string;
    assigned_to?: number | string;
}

interface UploadedFileTarget {
    fileId: number;
    filename: string;
    linkedProducts: string[];
}

interface UploadedFilesResult extends Record<string, unknown> {
    ok: boolean;
    files?: UploadedFileTarget[];
    error?: string;
}

interface FileScopeRecord {
    global: { company_id: number; company_name?: string } | null;
    projects: Array<{ project_id: number; project_name: string }>;
    features: Array<{ feature_id: number; feature_name?: string }>;
}

interface GenerateWorkProductArgs {
    elementId?: string;
    workProductId?: string;
    workProductName?: string;
    targetFeatureName?: string;
    targetUserStoryName?: string;
    targetUserStoryNumber?: string;
    confirmationToken?: string;
    phase?: string;
    forceNewVersion?: boolean;
    scope?: {
        level: "PRODUCT" | "FEATURE" | "USERSTORY";
        featureId?: number;
        userStoryId?: string;
    };
}

export interface CurrentProductContext {
    projectId?: number | null;
    projectName?: string | null;
}

export interface CurrentWorkProductContext {
    workProductId?: string | null;
    workProductTitle?: string | null;
    workProductLevel?: string | null;
    workProductContentType?: string | null;
    workProductPhase?: string | null;
    editorSurface?: "ide" | "rich-text" | null;
    featureId?: string | number | null;
    featureName?: string | null;
    userStoryId?: string | null;
    userStoryTitle?: string | null;
}

export interface CurrentHierarchyContext {
    hierarchyLevel?: "product" | "feature" | "user_story" | null;
    hierarchyName?: string | null;
    projectId?: number | null;
    projectName?: string | null;
    featureId?: string | number | null;
    featureName?: string | null;
    userStoryId?: string | null;
    userStoryName?: string | null;
}

export type UpdateNewProductDraft = (patch: NewProductDraftPatch) =>
    | { ok: true; draft: NewProductDraftSnapshot }
    | { ok: false; error: string };

export type ActiveCreationForm = "product" | "context_mesh" | null;

interface UpdateNewProductDraftArgs {
    product_mode?: ProductKind;
    feature_scope?: FeatureScope;
    product_name?: string;
    problem_product_solves?: string;
    solution_product_offers?: string;
    primary_form_factor?: PrimaryFormFactor;
    target_users?: string[];
    team_members?: string[];
    user_confirmed: boolean;
    overwrite_existing?: boolean;
}

interface UpdatePrimaryFormFactorArgs {
    primary_form_factor: PrimaryFormFactor;
    user_confirmed: boolean;
    overwrite_existing: boolean;
}

async function fetchProjects(token: string): Promise<ProjectRecord[]> {
    const { projects } = await listUserProjectsApi(token);
    return projects as unknown as ProjectRecord[];
}

function matchByName(projects: ProjectRecord[], name: string): ProjectRecord[] {
    const needle = name.trim().toLowerCase();
    if (!needle) return [];
    const exact = projects.filter((p) => (p.project_name ?? "").trim().toLowerCase() === needle);
    if (exact.length > 0) return exact;
    const startsWith = projects.filter((p) => (p.project_name ?? "").trim().toLowerCase().startsWith(needle));
    if (startsWith.length > 0) return startsWith;
    return projects.filter((p) => (p.project_name ?? "").trim().toLowerCase().includes(needle));
}

function normalizeFuzzyName(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function editDistance(left: string, right: string): number {
    const matrix = Array.from({ length: left.length + 1 }, (_, row) =>
        Array.from({ length: right.length + 1 }, (_, column) => (row === 0 ? column : column === 0 ? row : 0))
    );

    for (let row = 1; row <= left.length; row += 1) {
        for (let column = 1; column <= right.length; column += 1) {
            const substitutionCost = left[row - 1] === right[column - 1] ? 0 : 1;
            matrix[row][column] = Math.min(
                matrix[row - 1][column] + 1,
                matrix[row][column - 1] + 1,
                matrix[row - 1][column - 1] + substitutionCost
            );

            // Treat a common adjacent-letter transposition as one typo.
            if (
                row > 1 &&
                column > 1 &&
                left[row - 1] === right[column - 2] &&
                left[row - 2] === right[column - 1]
            ) {
                matrix[row][column] = Math.min(matrix[row][column], matrix[row - 2][column - 2] + 1);
            }
        }
    }

    return matrix[left.length][right.length];
}

function closestProjectByName(projects: ProjectRecord[], requestedName: string): ProjectRecord | null {
    const needle = normalizeFuzzyName(requestedName);
    if (!needle) return null;

    const ranked = projects
        .filter((project) => Boolean(project.project_name?.trim()))
        .map((project) => {
            const words = normalizeFuzzyName(project.project_name ?? "").split(" ").filter(Boolean);
            const phrases = words.flatMap((_, start) =>
                words.slice(start).map((__, offset) => words.slice(start, start + offset + 1).join(" "))
            );
            const distance = Math.min(...phrases.map((phrase) => editDistance(needle, phrase)));
            const similarity = Math.max(
                ...phrases.map((phrase) => 1 - editDistance(needle, phrase) / Math.max(needle.length, phrase.length, 1))
            );
            return { project, distance, similarity };
        })
        .sort(
            (left, right) =>
                right.similarity - left.similarity ||
                left.distance - right.distance ||
                String(left.project.project_name).localeCompare(String(right.project.project_name)) ||
                left.project.project_id - right.project.project_id
        );

    return ranked[0]?.project ?? null;
}

function normalizePositiveId(value: unknown): number | null {
    if (value == null) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
}

function normalizeText(value: unknown): string {
    return String(value ?? "").trim().toLowerCase();
}

function normalizeScopedTargetValue(value: string): string {
    const trimmed = value.trim().replace(/[\s\u00A0]+/g, " ");
    const withoutBoundaryQuotes = trimmed.replace(/^["'`]+|["'`]+$/g, "");
    return withoutBoundaryQuotes.trim();
}

function normalizeParsedEntityName(value: string): string {
    const cleaned = normalizeScopedTargetValue(value);
    return cleaned.replace(/^(?:the|a|an)\s+/i, "").trim();
}

function parseScopedWorkProductName(value: string): {
    workProductName: string;
    targetFeatureName?: string;
    targetUserStoryName?: string;
} | null {
    const input = String(value || "").trim();
    if (!input) return null;

    const patterns: Array<{
        regex: RegExp;
        map: (match: RegExpMatchArray) => {
            workProductName: string;
            targetFeatureName?: string;
            targetUserStoryName?: string;
        };
    }> = [
        {
            regex: /^(.*?)\s+for\s+(?:the\s+)?feature\s+(.+?)\s+(?:with|and)\s+(?:the\s+)?(?:user\s*story|story)\s+(.+)$/i,
            map: (match) => ({
                workProductName: match[1] || "",
                targetFeatureName: match[2],
                targetUserStoryName: match[3]
            })
        },
        {
            regex: /^(.*?)\s+for\s+(?:the\s+)?(?:user\s*story|story)\s+(.+)$/i,
            map: (match) => ({
                workProductName: match[1] || "",
                targetUserStoryName: match[2]
            })
        },
        {
            regex: /^(.*?)\s+for\s+(.+?)\s+story$/i,
            map: (match) => ({
                workProductName: match[1] || "",
                targetUserStoryName: match[2]
            })
        },
        {
            regex: /^(.*?)\s+for\s+(?:the\s+)?feature\s+(.+)$/i,
            map: (match) => ({
                workProductName: match[1] || "",
                targetFeatureName: match[2]
            })
        }
    ];

    for (const pattern of patterns) {
        const match = input.match(pattern.regex);
        if (!match) continue;

        const mapped = pattern.map(match);
        const workProductName = normalizeScopedTargetValue(mapped.workProductName || "");
        const targetFeatureName = mapped.targetFeatureName
            ? normalizeParsedEntityName(mapped.targetFeatureName)
            : "";
        const targetUserStoryName = mapped.targetUserStoryName
            ? normalizeParsedEntityName(mapped.targetUserStoryName)
            : "";

        if (!workProductName || workProductName.length < 3) continue;
        if (!targetFeatureName && !targetUserStoryName) continue;

        return {
            workProductName,
            ...(targetFeatureName ? { targetFeatureName } : {}),
            ...(targetUserStoryName ? { targetUserStoryName } : {})
        };
    }

    return null;
}

const SCOPE_LEVEL_RANK: Record<"PRODUCT" | "FEATURE" | "USERSTORY", number> = {
    PRODUCT: 0,
    FEATURE: 1,
    USERSTORY: 2
};

function isSameOrHigherScopeLevel(
    current: "PRODUCT" | "FEATURE" | "USERSTORY",
    requested: "PRODUCT" | "FEATURE" | "USERSTORY"
): boolean {
    return SCOPE_LEVEL_RANK[requested] <= SCOPE_LEVEL_RANK[current];
}

function canUseRequestedScopeFromCurrent(
    current: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null },
    requested: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null }
): boolean {
    if (!isSameOrHigherScopeLevel(current.level, requested.level)) return false;
    if (requested.level === "PRODUCT") return true;
    if (requested.level === "FEATURE") return requested.featureId != null && requested.featureId === current.featureId;
    return (
        requested.featureId != null &&
        requested.featureId === current.featureId &&
        Boolean(requested.userStoryId) &&
        requested.userStoryId === current.userStoryId
    );
}

function buildWorkProductCandidatePrompt(candidates: WorkProductConfirmationCandidate[]): string {
    if (candidates.length < 2) return "Please confirm this generation request.";
    const options = candidates
        .slice(0, 3)
        .map(candidate => `${candidate.option}) ${candidate.workProduct.name}`)
        .join("; ");
    const choiceText = candidates.length === 2 ? "1 or 2" : "1, 2, or 3";
    return `I found a few close matches. Reply with ${choiceText} to choose: ${options}.`;
}

function isUnauthorizedFilePath(value: string): boolean {
    const normalized = value.trim().replace(/\\/g, "/");
    return normalized.startsWith("/") || /^[a-z]:\//i.test(normalized) || normalized.split("/").includes("..");
}

function fileBasename(value: string): string {
    const normalized = value.trim().replace(/\\/g, "/");
    return normalized.split("/").filter(Boolean).pop()?.toLowerCase() ?? "";
}

function resolveUploadedFileTarget(files: UploadedFileTarget[], requestedPath: string) {
    if (!requestedPath.trim()) {
        return {
            ok: false,
            clarification_required: true,
            reason: "ambiguous_path",
            error: "Specify the exact uploaded filename to delete."
        };
    }
    if (isUnauthorizedFilePath(requestedPath)) {
        return {
            ok: false,
            reason: "unauthorized_path",
            error: "Only files in your Files & Attachments workspace can be deleted."
        };
    }

    const needle = fileBasename(requestedPath);
    const exact = files.filter(file => file.filename.trim().toLowerCase() === needle);
    const candidates = exact.length > 0
        ? exact
        : files.filter(file => file.filename.trim().toLowerCase().includes(needle));

    if (candidates.length === 0) {
        return {
            ok: false,
            reason: "not_found",
            error: `No uploaded file matching "${requestedPath}" was found.`
        };
    }
    if (candidates.length > 1) {
        return {
            ok: false,
            clarification_required: true,
            reason: "ambiguous_path",
            error: `"${requestedPath}" matches more than one uploaded file. Ask the user to choose one.`,
            candidates
        };
    }
    return { ok: true, target: candidates[0] };
}

function resolveAssignmentFile(files: Array<{ fileid: number; filename: string }>, requestedName: string) {
    const needle = fileBasename(requestedName);
    if (!needle) {
        return { ok: false, clarification_required: true, reason: "ambiguous_file", error: "Specify the uploaded filename to assign." };
    }
    const exact = files.filter(file => file.filename.trim().toLowerCase() === needle);
    const candidates = exact.length > 0
        ? exact
        : files.filter(file => file.filename.trim().toLowerCase().includes(needle));
    if (candidates.length === 0) {
        return { ok: false, reason: "file_not_found", error: `No uploaded file matching "${requestedName}" was found.` };
    }
    if (candidates.length > 1) {
        return {
            ok: false,
            clarification_required: true,
            reason: "ambiguous_file",
            error: `"${requestedName}" matches more than one uploaded file. Ask the user to choose one.`,
            candidates: candidates.map(file => file.filename)
        };
    }
    return { ok: true, target: candidates[0] };
}

function resolveAssignmentProduct(projects: ProjectRecord[], requestedName: string) {
    const candidates = matchByName(projects, requestedName);
    if (candidates.length === 0) {
        return { ok: false, reason: "product_not_found", error: `No product matching "${requestedName}" was found.` };
    }
    if (candidates.length > 1) {
        return {
            ok: false,
            clarification_required: true,
            reason: "ambiguous_product",
            error: `"${requestedName}" matches more than one product. Ask the user to choose one.`,
            candidates: candidates.map(project => project.project_name)
        };
    }
    return { ok: true, target: candidates[0] };
}

async function fetchFileScope(token: string, fileId: number): Promise<FileScopeRecord> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/get_file_scopes`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_ids: [fileId] })
    });
    if (!response.ok) throw new Error("The file's current assignments could not be loaded. Try again.");
    const payload = await response.json();
    const scope = payload?.scopes?.[fileId] ?? payload?.scopes?.[String(fileId)];
    return {
        global: scope?.global ?? null,
        projects: Array.isArray(scope?.projects) ? scope.projects : [],
        features: Array.isArray(scope?.features) ? scope.features : []
    };
}

async function loadUploadedFilesWithProducts(
    getToken: () => Promise<string>,
    userId?: number | null
): Promise<UploadedFilesResult> {
    if (!userId) {
        return { ok: false, error: "Your user profile is not available. Refresh the page and try again." };
    }

    try {
        const token = await getToken();
        const filesResponse = await fetchWithAuth(
            `${BACKEND_URI}/api/vector_files/get_all_user_files/${userId}`,
            token,
            { method: "GET" }
        );
        if (!filesResponse.ok) {
            return { ok: false, error: "Files could not be loaded. Check your connection and try again." };
        }

        const filesPayload = await filesResponse.json();
        const files = Array.isArray(filesPayload?.files) ? filesPayload.files : [];
        if (files.length === 0) return { ok: true, files: [] };

        const fileIds = files.map((file: { fileid: number }) => file.fileid);
        const scopesResponse = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/get_file_scopes`, token, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ file_ids: fileIds })
        });
        if (!scopesResponse.ok) {
            return { ok: false, error: "Files loaded, but their linked products could not be loaded. Try again." };
        }

        const scopesPayload = await scopesResponse.json();
        const scopes = scopesPayload?.scopes ?? {};
        return {
            ok: true,
            files: files.map((file: { fileid: number; filename: string }) => {
                const scope = scopes[file.fileid] ?? scopes[String(file.fileid)];
                const projects = Array.isArray(scope?.projects) ? scope.projects : [];
                const isGlobal = !scope || Boolean(scope.global) || (projects.length === 0 && (scope.features?.length ?? 0) === 0);
                return {
                    fileId: file.fileid,
                    filename: file.filename,
                    linkedProducts: isGlobal
                        ? ["All products"]
                        : projects.map((project: { project_name: string }) => project.project_name)
                };
            })
        };
    } catch {
        return { ok: false, error: "Files could not be loaded. Check your connection and try again." };
    }
}

function isQuestionStyleRefreshInput(input: string): boolean {
    const value = String(input || "").trim().toLowerCase();
    if (!value) return false;
    if (value.includes("?")) return true;
    const readMarkers = [
        "what",
        "show",
        "list",
        "current",
        "existing",
        "which",
        "display",
        "can i see",
        "what are",
        "what is"
    ];
    return readMarkers.some((marker) => value.includes(marker));
}

function formatWorkProductLevelLabel(level: string | null): string | null {
    if (!level) return null;
    const normalized = level.trim().toUpperCase();
    if (normalized === "USER_STORY") return "user story";
    if (normalized === "FEATURE") return "feature";
    if (normalized === "PRODUCT") return "product";
    return level.trim().toLowerCase().replace(/_/g, " ");
}

function formatHierarchyLevelLabel(level: "product" | "feature" | "user_story"): string {
    if (level === "user_story") return "user story";
    return level;
}

function formatHierarchyEntityType(level: "product" | "feature" | "user_story"): string {
    if (level === "user_story") return "user story";
    return level;
}

function featureIdOf(feature: FeatureRecord): number | null {
    const raw = feature.featureid ?? feature.id;
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

function featureNameOf(feature: FeatureRecord): string {
    return feature.title || feature.name || "Untitled Feature";
}

function matchFeaturesByName(features: FeatureRecord[], name: string): FeatureRecord[] {
    const needle = normalizeText(name);
    if (!needle) return [];
    const exact = features.filter((f) => normalizeText(featureNameOf(f)) === needle);
    if (exact.length > 0) return exact;
    return features.filter((f) => normalizeText(featureNameOf(f)).includes(needle));
}

function storyIdOf(story: UserStoryRecord): string {
    return String(story.id);
}

function storyNumberOf(story: UserStoryRecord): string {
    return String(story.story_number ?? "");
}

function storyTitleOf(story: UserStoryRecord): string {
    return String(story.title ?? "");
}

function matchStoriesByTitle(stories: UserStoryRecord[], title: string): UserStoryRecord[] {
    const needle = normalizeText(title);
    if (!needle) return [];
    const exact = stories.filter((s) => normalizeText(storyTitleOf(s)) === needle);
    if (exact.length > 0) return exact;
    return stories.filter((s) => normalizeText(storyTitleOf(s)).includes(needle));
}

/**
 * Build the chat tool set. Tools execute in the browser and call the
 * Intelligenic backend with the user's Clerk-issued token (same token the
 * chat itself uses).
 *
 * Cast pattern mirrors examples/react-chat-demo — each tool is typed
 * narrowly via generics on `defineChatTool`, then the array is widened to
 * `ChatTool[]` for `<CodeNexusChat tools=...>`. Function-arg contravariance
 * makes the direct assignment unsound from TS's POV, but the runtime
 * dispatch is keyed by `name` so the cast is safe.
 */
export function createChatTools(
    getToken: () => Promise<string>,
    getCurrentProduct?: () => CurrentProductContext | null | undefined,
    getCurrentWorkProduct?: () => CurrentWorkProductContext | null | undefined,
    getCurrentHierarchy?: () => CurrentHierarchyContext | null | undefined,
    getNewProductDraft?: () => NewProductDraftSnapshot | null | undefined,
    updateNewProductDraft?: UpdateNewProductDraft,
    userId?: number | null,
    disconnectStateStore?: DisconnectStateStore,
    requestWorkProductGeneration?: RequestWorkProductGeneration,
    getActiveCreationForm?: () => ActiveCreationForm
): ChatTool[] {
    let activeRefreshSessionId: number | null = null;
    let activeRefreshTarget: { projectId: number | null; projectName: string | null } | null = null;
    const resolvedDeletionTargets = new Map<number, UploadedFileTarget>();

    const getCurrentProductSnapshot = (): CurrentProductContext | null => {
        if (!getCurrentProduct) return null;
        const current = getCurrentProduct();
        if (!current) return null;
        const hasId = typeof current.projectId === "number" && Number.isFinite(current.projectId);
        const hasName = typeof current.projectName === "string" && current.projectName.trim().length > 0;
        if (!hasId && !hasName) return null;
        return {
            projectId: hasId ? current.projectId ?? null : null,
            projectName: hasName ? current.projectName?.trim() ?? null : null
        };
    };

    const getCurrentWorkProductSnapshot = (): CurrentWorkProductContext | null => {
        if (!getCurrentWorkProduct) return null;
        const current = getCurrentWorkProduct();
        if (!current) return null;

        const workProductId =
            typeof current.workProductId === "string" && current.workProductId.trim().length > 0
                ? current.workProductId.trim()
                : null;
        const workProductTitle =
            typeof current.workProductTitle === "string" && current.workProductTitle.trim().length > 0
                ? current.workProductTitle.trim()
                : null;
        const workProductLevel =
            typeof current.workProductLevel === "string" && current.workProductLevel.trim().length > 0
                ? current.workProductLevel.trim()
                : null;
        const workProductContentType =
            typeof current.workProductContentType === "string" && current.workProductContentType.trim().length > 0
                ? current.workProductContentType.trim()
                : null;
        const workProductPhase =
            typeof current.workProductPhase === "string" && current.workProductPhase.trim().length > 0
                ? current.workProductPhase.trim()
                : null;
        const editorSurface =
            current.editorSurface === "ide" || current.editorSurface === "rich-text"
                ? current.editorSurface
                : null;

        const featureId = current.featureId ?? null;
        const hasFeatureId =
            typeof featureId === "number"
                ? Number.isFinite(featureId)
                : typeof featureId === "string"
                  ? featureId.trim().length > 0
                  : false;

        const userStoryId =
            typeof current.userStoryId === "string" && current.userStoryId.trim().length > 0
                ? current.userStoryId.trim()
                : null;
        const featureName =
            typeof current.featureName === "string" && current.featureName.trim().length > 0
                ? current.featureName.trim()
                : null;
        const userStoryTitle =
            typeof current.userStoryTitle === "string" && current.userStoryTitle.trim().length > 0
                ? current.userStoryTitle.trim()
                : null;

        if (!workProductId && !workProductTitle && !workProductLevel && !workProductContentType && !workProductPhase && !editorSurface && !hasFeatureId && !featureName && !userStoryId && !userStoryTitle) {
            return null;
        }

        return {
            workProductId,
            workProductTitle,
            workProductLevel,
            workProductContentType,
            workProductPhase,
            editorSurface,
            featureId: hasFeatureId ? featureId : null,
            featureName,
            userStoryId,
            userStoryTitle
        };
    };

    const getCurrentHierarchySnapshot = (): CurrentHierarchyContext | null => {
        if (!getCurrentHierarchy) return null;
        const current = getCurrentHierarchy();
        if (!current) return null;

        const level = current.hierarchyLevel;
        const validLevel = level === "product" || level === "feature" || level === "user_story" ? level : null;
        const hierarchyName =
            typeof current.hierarchyName === "string" && current.hierarchyName.trim().length > 0
                ? current.hierarchyName.trim()
                : null;

        if (!validLevel || !hierarchyName) return null;

        return {
            hierarchyLevel: validLevel,
            hierarchyName,
            projectId: current.projectId ?? null,
            projectName: current.projectName ?? null,
            featureId: current.featureId ?? null,
            featureName: current.featureName ?? null,
            userStoryId: current.userStoryId ?? null,
            userStoryName: current.userStoryName ?? null
        };
    };

    const resolveProjectFromCurrentContext = (projects: ProjectRecord[]): ProjectRecord | null => {
        const current = getCurrentProductSnapshot();
        if (!current) return null;

        if (current.projectId != null) {
            const byId = projects.find((p) => p.project_id === current.projectId);
            if (byId) return byId;
        }

        if (current.projectName) {
            const matches = matchByName(projects, current.projectName);
            if (matches.length === 1) return matches[0];
        }

        return null;
    };

    const resolveProjectTarget = (
        projects: ProjectRecord[],
        args: { projectId?: number; projectName?: string }
    ): {
        target: ProjectRecord | null;
        error?: string;
        matches?: Array<{ id: number; name?: string }>;
        closestMatch?: { id: number; name?: string };
    } => {
        const requestedProjectName = typeof args.projectName === "string" ? args.projectName.trim() : "";

        // A product name in the current tool call represents the user's latest
        // target. Resolve it before an id so a stale id remembered from an
        // earlier navigation cannot override a newly requested product name.
        if (requestedProjectName) {
            const matches = matchByName(projects, requestedProjectName);
            if (matches.length === 0) {
                const closestProject = closestProjectByName(projects, requestedProjectName);
                const closestMatch = closestProject
                    ? { id: closestProject.project_id, name: closestProject.project_name }
                    : undefined;
                return {
                    target: null,
                    error: closestMatch?.name
                        ? `No product matched "${requestedProjectName}". Did you mean "${closestMatch.name}"? Ask the user to confirm before navigating.`
                        : `No product matched "${requestedProjectName}". Call list_projects to see what's available.`,
                    ...(closestMatch ? { closestMatch } : {})
                };
            }
            if (matches.length > 1) {
                return {
                    target: null,
                    error: `Multiple products matched "${requestedProjectName}". Ask the user which one.`,
                    matches: matches.map((p) => ({ id: p.project_id, name: p.project_name }))
                };
            }
            return { target: matches[0] };
        }

        const requestedProjectId = normalizePositiveId(args.projectId);
        if (requestedProjectId != null) {
            const byId = projects.find((p) => p.project_id === requestedProjectId);
            if (byId) {
                return { target: byId };
            }
            return {
                target: null,
                error: `No product matched id ${requestedProjectId}. Call list_projects to see what's available.`
            };
        }

        const currentProject = resolveProjectFromCurrentContext(projects);
        if (currentProject) {
            return { target: currentProject };
        }

        return {
            target: null,
            error: "Product is required. Call list_projects, show the user the options, and ask which product they mean."
        };
    };

    const get_current_product = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "get_current_product",
        category: "read",
        description:
            "Returns the currently open product in the app view, if available. " +
            "Use this when the user asks things like 'Which product am I in?' or " +
            "'What product is currently open?'.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async () => {
            const current = getCurrentProductSnapshot();
            if (!current) {
                return {
                    hasCurrentProduct: false,
                    message: "No currently open product context is available from this view."
                };
            }

            return {
                hasCurrentProduct: true,
                projectId: current.projectId ?? null,
                projectName: current.projectName ?? null
            };
        }
    });

    const get_new_product_draft = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "get_new_product_draft",
        category: "read",
        description:
            "Returns the current Add New Product form draft and canonical Context Mesh analysis status. " +
            "Use get_context_mesh_draft instead on the standalone Create Context Mesh form. This call establishes a new-task boundary: " +
            "after it returns, discuss only the current draft or the user's current request and do not mention or continue " +
            "actions from before the new-product screen was opened, including integration connection or disconnection actions.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async () => {
            const draft = getNewProductDraft?.();
            if (!draft) {
                return {
                    hasDraft: false,
                    message: "No active product or Context Mesh creation draft is available."
                };
            }
            return {
                hasDraft: true,
                ...draft
            };
        }
    });

    const update_new_product_draft = defineChatTool<UpdateNewProductDraftArgs, AppChatEventMap>({
        name: "update_new_product_draft",
        category: "mutation",
        description:
            "Writes user-confirmed values into the Add New Product form. Use update_context_mesh_draft instead on Create Context Mesh. " +
            "If the form is not active, this tool opens /products/new and waits for the form before writing. " +
            "Set product_mode only when Product Type is unset or the confirmed proposal changes it; when omitted on an unselected standalone build form it defaults to new. " +
            "Set feature_scope only for a confirmed scope change when Product Type is Add to Existing Product. " +
            "Call get_new_product_draft first, propose the exact visible field values in chat, and wait for the user to confirm in a later turn; the initial change request is not confirmation. " +
            "Set user_confirmed=true only after that confirmation. Send only fields in the confirmed proposal; never send blank strings or empty arrays as placeholders. Omitted fields are preserved. " +
            "Set overwrite_existing=true only when the user explicitly confirms revising populated fields. " +
            "Primary Form Factor defaults to web, so changing it to mobile or api requires overwrite_existing=true. For that field alone, omit product_mode, feature_scope, and all unrelated fields.",
        parameters: {
            type: "object",
            properties: {
                product_mode: {
                    type: "string",
                    enum: ["new", "existing"],
                    description:
                        "Value for Product Type (New vs Existing product). Setting it when unset unlocks the form."
                },
                feature_scope: {
                    type: "string",
                    enum: ["single", "multi"],
                    description: "Select Single Feature or Multiple Features only when the user explicitly confirms a scope change for Add to Existing Product. Omit for New Product and all unrelated field updates."
                },
                product_name: { type: "string", description: "Value for Product Name." },
                problem_product_solves: { type: "string", description: "Value for Problem Product Solves." },
                solution_product_offers: {
                    type: "string",
                    description: "Value for What Solution Does The Product Offer."
                },
                target_users: {
                    type: "array",
                    items: { type: "string" },
                    description: "Replacement values for Target Users."
                },
                team_members: {
                    type: "array",
                    items: { type: "string" },
                    description: "Replacement values for Team Members."
                },
                user_confirmed: {
                    type: "boolean",
                    description: "True only after the user confirms the proposed form values in chat."
                },
                overwrite_existing: {
                    type: "boolean",
                    default: false,
                    description: "Allow explicitly confirmed revisions or clearing of populated fields."
                }
            },
            required: ["user_confirmed"],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            if (args.user_confirmed !== true) {
                return {
                    ok: false,
                    error: "The creation form was not updated because the user has not confirmed the proposal."
                };
            }

            const fieldKeys = [
                "product_mode",
                "feature_scope",
                "product_name",
                "problem_product_solves",
                "solution_product_offers",
                "primary_form_factor",
                "target_users",
                "team_members"
            ] as const;
            if (!fieldKeys.some(key => Object.prototype.hasOwnProperty.call(args, key))) {
                return { ok: false, error: "Provide at least one Product Creation field to update." };
            }

            let current = getNewProductDraft?.();
            if (!current) {
                const requestedMode = args.product_mode ?? "new";
                ctx?.emit("navigate_to_new_product", { product_mode: requestedMode });

                // Navigation is handled by React after this tool event. Wait for
                // the new screen's imperative form ref rather than making the
                // model remember a fragile navigate-then-retry sequence.
                const deadline = Date.now() + 2000;
                while (!current && Date.now() < deadline) {
                    await new Promise(resolve => setTimeout(resolve, 25));
                    current = getNewProductDraft?.();
                }
                if (!current) {
                    return {
                        ok: false,
                        error: "The Product Creation form could not be opened. Please try again."
                    };
                }
            }

            const patch: NewProductDraftPatch = {};
            const appliedFields: string[] = [];
            const unchangedFields: string[] = [];
            const protectedFields: string[] = [];
            const ignoredFields: string[] = [];
            const overwriteExisting = args.overwrite_existing === true;
            const hasOwn = (key: keyof UpdateNewProductDraftArgs) => Object.prototype.hasOwnProperty.call(args, key);
            const paddedFieldValues: ReadonlyArray<readonly [keyof UpdateNewProductDraftArgs, unknown]> = [
                ["product_name", current.fields.name],
                ["problem_product_solves", current.fields.problem],
                ["solution_product_offers", current.fields.solution],
                ["target_users", current.users],
                ["team_members", current.team]
            ];
            const isEmptyPlaceholder = (value: unknown) =>
                (typeof value === "string" && value === "") || (Array.isArray(value) && value.length === 0);
            const matchesCurrentValue = (value: unknown, currentValue: unknown) =>
                JSON.stringify(value) === JSON.stringify(currentValue);
            // Some models expand an otherwise minimal Primary Form Factor update
            // into every optional form field, using either empty placeholders or
            // copies of the current draft plus an invalid New Product feature
            // scope. Treat only that exact, otherwise-unchanged envelope as
            // padding so genuine multi-field edits keep their existing behavior.
            const paddedPrimaryFormFactorUpdate =
                hasOwn("primary_form_factor") &&
                args.primary_form_factor !== current.primaryFormFactor &&
                args.product_mode === "new" &&
                current.kind === "new" &&
                hasOwn("feature_scope") &&
                paddedFieldValues.every(
                    ([key, currentValue]) =>
                        hasOwn(key) &&
                        (isEmptyPlaceholder(args[key]) || matchesCurrentValue(args[key], currentValue))
                );

            const stageField = <K extends keyof NewProductDraftPatch>(
                label: string,
                patchKey: K,
                currentValue: unknown,
                nextValue: NewProductDraftPatch[K]
            ) => {
                if (JSON.stringify(currentValue) === JSON.stringify(nextValue)) {
                    unchangedFields.push(label);
                    return;
                }
                const populated = Array.isArray(currentValue)
                    ? currentValue.some(value => value.trim() !== "")
                    : typeof currentValue === "string" && currentValue.trim() !== "";
                if (populated && !overwriteExisting) {
                    protectedFields.push(label);
                    return;
                }
                patch[patchKey] = nextValue;
                appliedFields.push(label);
            };

            const requestedMode = args.product_mode ?? (current.kind === null ? "new" : undefined);
            if (requestedMode !== undefined) {
                // current.kind is null while the gate is unanswered; undefined keeps
                // stageField's populated-check false so the first write always applies.
                stageField("Product Type", "kind", current.kind ?? undefined, requestedMode);
            }
            if (Object.prototype.hasOwnProperty.call(args, "feature_scope")) {
                const effectiveMode = requestedMode ?? current.kind;
                if (effectiveMode !== "existing") {
                    // Feature Scope has no meaning for a New Product. Models can
                    // still pad optional tool arguments, so ignore this field
                    // instead of rejecting every otherwise-valid draft update.
                    ignoredFields.push("Feature Scope");
                } else {
                    stageField("Feature Scope", "scope", current.scope ?? undefined, args.feature_scope);
                }
            }
            if (Object.prototype.hasOwnProperty.call(args, "product_name") && !paddedPrimaryFormFactorUpdate) {
                stageField("Product Name", "productName", current.fields.name, args.product_name ?? "");
            }
            if (Object.prototype.hasOwnProperty.call(args, "problem_product_solves") && !paddedPrimaryFormFactorUpdate) {
                stageField(
                    "Problem Product Solves",
                    "problemProductSolves",
                    current.fields.problem,
                    args.problem_product_solves ?? ""
                );
            }
            if (Object.prototype.hasOwnProperty.call(args, "solution_product_offers") && !paddedPrimaryFormFactorUpdate) {
                stageField(
                    "What Solution Does The Product Offer",
                    "solutionProductOffers",
                    current.fields.solution,
                    args.solution_product_offers ?? ""
                );
            }
            if (Object.prototype.hasOwnProperty.call(args, "primary_form_factor")) {
                stageField(
                    "Primary Form Factor",
                    "primaryFormFactor",
                    current.primaryFormFactor,
                    args.primary_form_factor ?? null
                );
            }
            if (Object.prototype.hasOwnProperty.call(args, "target_users") && !paddedPrimaryFormFactorUpdate) {
                stageField("Target Users", "targetUsers", current.users, args.target_users ?? []);
            }
            if (Object.prototype.hasOwnProperty.call(args, "team_members") && !paddedPrimaryFormFactorUpdate) {
                stageField("Team Members", "teamMembers", current.team, args.team_members ?? []);
            }
            if (paddedPrimaryFormFactorUpdate) {
                ignoredFields.push(
                    "Product Name",
                    "Problem Product Solves",
                    "What Solution Does The Product Offer",
                    "Target Users",
                    "Team Members"
                );
            }

            let updatedDraft = current;
            if (appliedFields.length > 0) {
                if (!updateNewProductDraft) {
                    return {
                        ok: false,
                        error: "The active creation form cannot accept FDE updates from this app version."
                    };
                }
                const result = updateNewProductDraft(patch);
                if (!result.ok) return result;
                updatedDraft = result.draft;
            }

            const status =
                appliedFields.length > 0
                    ? protectedFields.length > 0
                        ? "partially_applied"
                        : "applied"
                    : protectedFields.length > 0
                      ? "protected"
                      : "unchanged";
            return {
                ok: status !== "protected",
                status,
                fully_applied: protectedFields.length === 0,
                applied_fields: appliedFields,
                unchanged_fields: unchangedFields,
                protected_fields: protectedFields,
                ignored_fields: ignoredFields,
                updated_draft: updatedDraft,
                message:
                    protectedFields.length > 0
                        ? appliedFields.length > 0
                            ? "Some populated fields were protected. Ask the user to explicitly confirm revisions before retrying with overwrite_existing=true."
                            : "No fields were updated because populated fields are protected. Ask the user to explicitly confirm revisions before retrying with overwrite_existing=true."
                        : appliedFields.length > 0
                          ? "The confirmed creation-form fields were updated."
                          : "The active creation form already contains the confirmed values."
            };
        }
    });

    const get_context_mesh_draft = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "get_context_mesh_draft",
        category: "read",
        description:
            "Returns the live standalone Create Context Mesh form draft and canonical analysis status. " +
            "Use this instead of get_new_product_draft on /context-meshes/new.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async () => {
            // Navigation and the chat tool call run in the same turn. Give React
            // time to mount the destination form and publish its live draft.
            const deadline = Date.now() + 2000;
            let active = getActiveCreationForm?.() === "context_mesh";
            let draft = active ? getNewProductDraft?.() : null;
            while ((!active || !draft) && Date.now() < deadline) {
                await new Promise(resolve => setTimeout(resolve, 25));
                active = getActiveCreationForm?.() === "context_mesh";
                draft = active ? getNewProductDraft?.() : null;
            }
            if (!active) {
                return {
                    hasDraft: false,
                    message: "The standalone Create Context Mesh form is not active."
                };
            }
            if (!draft) {
                return {
                    hasDraft: false,
                    message: "The Create Context Mesh form is still loading."
                };
            }
            return { hasDraft: true, ...draft };
        }
    });

    const update_context_mesh_draft = defineChatTool<UpdateNewProductDraftArgs, AppChatEventMap>({
        name: "update_context_mesh_draft",
        category: "mutation",
        description:
            "Writes user-confirmed values into the active standalone Create Context Mesh form. " +
            "Call get_context_mesh_draft first, propose exact values, and wait for confirmation in a later turn. " +
            "This tool updates only the Context Mesh draft and never creates or modifies a Product.",
        parameters: (update_new_product_draft as any).parameters,
        execute: async (args, ctx) => {
            if (getActiveCreationForm?.() !== "context_mesh") {
                return {
                    ok: false,
                    error: "The standalone Create Context Mesh form is not active."
                };
            }
            return (update_new_product_draft as any).execute(args, ctx);
        }
    });

    const update_primary_form_factor = defineChatTool<UpdatePrimaryFormFactorArgs, AppChatEventMap>({
        name: "update_primary_form_factor",
        category: "mutation",
        description:
            "Changes only Primary Form Factor on the active Add New Product or Create Context Mesh form. " +
            "Use this tool instead of update_new_product_draft for every Primary Form Factor change. " +
            "Call the matching draft read tool first, propose Web, Mobile, or API using the visible label, end the turn, and wait for explicit confirmation in a later user turn. " +
            "Set user_confirmed=true only after that confirmation. Because Web is preselected, changing it requires overwrite_existing=true. " +
            "This narrow tool cannot change Product Type, Feature Scope, or any other draft field.",
        parameters: {
            type: "object",
            properties: {
                primary_form_factor: {
                    type: "string",
                    enum: ["web", "mobile", "api"],
                    description: "The confirmed Primary Form Factor value: web, mobile, or api."
                },
                user_confirmed: {
                    type: "boolean",
                    description: "True only after the user confirms the proposed Primary Form Factor in a later turn."
                },
                overwrite_existing: {
                    type: "boolean",
                    description: "True when replacing the currently selected Primary Form Factor."
                }
            },
            required: ["primary_form_factor", "user_confirmed", "overwrite_existing"],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            if (args.user_confirmed !== true) {
                return {
                    ok: false,
                    error: "Primary Form Factor was not updated because the user has not confirmed the creation-form proposal."
                };
            }

            let current = getNewProductDraft?.();
            if (!current) {
                ctx?.emit("navigate_to_new_product", { product_mode: "new" });
                const deadline = Date.now() + 2000;
                while (!current && Date.now() < deadline) {
                    await new Promise(resolve => setTimeout(resolve, 25));
                    current = getNewProductDraft?.();
                }
                if (!current) {
                    return {
                        ok: false,
                        error: "The Product Creation form could not be opened. Please try again."
                    };
                }
            }

            if (current.primaryFormFactor === args.primary_form_factor) {
                return {
                    ok: true,
                    status: "unchanged",
                    fully_applied: true,
                    applied_fields: [],
                    unchanged_fields: ["Primary Form Factor"],
                    protected_fields: [],
                    updated_draft: current,
                    message: "Primary Form Factor already contains the confirmed value."
                };
            }

            if (current.primaryFormFactor !== null && args.overwrite_existing !== true) {
                return {
                    ok: false,
                    status: "protected",
                    fully_applied: false,
                    applied_fields: [],
                    unchanged_fields: [],
                    protected_fields: ["Primary Form Factor"],
                    updated_draft: current,
                    message: "Primary Form Factor is already selected. Retry only after confirmation with overwrite_existing=true."
                };
            }

            if (!updateNewProductDraft) {
                return {
                    ok: false,
                    error: "The active creation form cannot accept FDE updates from this app version."
                };
            }
            const result = updateNewProductDraft({ primaryFormFactor: args.primary_form_factor });
            if (!result.ok) return result;

            return {
                ok: true,
                status: "applied",
                fully_applied: true,
                applied_fields: ["Primary Form Factor"],
                unchanged_fields: [],
                protected_fields: [],
                updated_draft: result.draft,
                message: "The confirmed Primary Form Factor was updated."
            };
        }
    });

    const get_current_work_product = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "get_current_work_product",
        category: "read",
        description:
            "Returns the currently open work product in the editor, if available. " +
            "Use this when the user asks things like 'Which work product am I in?' or " +
            "'What work product is currently open?'.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async () => {
            const current = getCurrentWorkProductSnapshot();
            if (!current) {
                return {
                    hasCurrentWorkProduct: false,
                    message: "No currently open work product context is available right now."
                };
            }

            return {
                hasCurrentWorkProduct: true,
                workProductId: current.workProductId ?? null,
                workProductTitle: current.workProductTitle ?? null,
                workProductLevel: current.workProductLevel ?? null,
                workProductLevelLabel: formatWorkProductLevelLabel(current.workProductLevel ?? null),
                featureId: current.featureId ?? null,
                featureName: current.featureName ?? null,
                userStoryId: current.userStoryId ?? null,
                userStoryTitle: current.userStoryTitle ?? null,
                featureContextMessage: current.featureName ? `You're in feature "${current.featureName}".` : null
            };
        }
    });

    const get_current_hierarchy = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "get_current_hierarchy",
        category: "read",
        description:
            "Returns the currently selected hierarchy context in Feature Studio. " +
            "Use this when the user asks things like 'What level of the product hierarchy am I in?' " +
            "or 'Which feature am I in?'.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async () => {
            const current = getCurrentHierarchySnapshot();
            if (!current || !current.hierarchyLevel || !current.hierarchyName) {
                return {
                    hasCurrentHierarchy: false,
                    message: "No current product hierarchy context is available right now."
                };
            }

            const levelLabel = formatHierarchyLevelLabel(current.hierarchyLevel);
            const entityType = formatHierarchyEntityType(current.hierarchyLevel);
            return {
                hasCurrentHierarchy: true,
                hierarchyLevel: current.hierarchyLevel,
                hierarchyLevelLabel: levelLabel,
                hierarchyName: current.hierarchyName,
                message: `You are at the ${levelLabel} level, and in the ${entityType} "${current.hierarchyName}".`,
                projectId: current.projectId ?? null,
                projectName: current.projectName ?? null,
                featureId: current.featureId ?? null,
                featureName: current.featureName ?? null,
                userStoryId: current.userStoryId ?? null,
                userStoryName: current.userStoryName ?? null
            };
        }
    });

    const list_projects = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "list_projects",
        category: "read",
        description:
            "Returns the current user's products in Intelligenic. " +
            "Call this when the user asks to see their products, or when you " +
            "need to resolve a product name to an id for another tool. Returns id, name, " +
            "status, description, and last_updated for each product. " +
            "IMPORTANT: When presenting products to users, DO NOT include the ID numbers. " +
            "Only show: product name, status (if present), description, and last updated date.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async () => {
            const token = await getToken();
            const projects = await fetchProjects(token);
            return {
                count: projects.length,
                projects: projects.map((p) => ({
                    id: p.project_id,
                    name: p.project_name,
                    status: p.status,
                    description: p.project_description ?? null,
                    last_updated: p.last_updated ?? null
                }))
            };
        }
    });

    const list_user_stories = defineChatTool<
        { projectId?: number; projectName?: string; openOnly?: boolean },
        AppChatEventMap
    >({
        name: "list_user_stories",
        description:
            "Returns user stories (tickets) for a single product. Pass projectId OR projectName. " +
            "If neither is passed, this tool will use the currently open product context when available. " +
            "If no current product context exists, call list_projects and ask the user which one they mean. " +
            "By default `openOnly` is true and excludes completed/archived/cancelled tickets. " +
            "Returns up to " +
            MAX_STORIES +
            " stories with story_number, title, status, priority, and assigned_to.",
        category: "read",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number", description: "Numeric project id from list_projects." },
                projectName: { type: "string", description: "Project name. Used only if projectId not provided." },
                openOnly: {
                    type: "boolean",
                    description: "If true (default), exclude completed/archived/cancelled stories.",
                    default: true
                }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args) => {
            const token = await getToken();

            const projects = await fetchProjects(token);
            const resolved = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolved.target) {
                return {
                    error: resolved.error,
                    ...(resolved.matches ? { matches: resolved.matches } : {})
                };
            }
            const projectId = resolved.target.project_id;

            const stories = await getUserStoriesByProject(projectId, token, false, true);
            const openOnly = args.openOnly !== false;
            const filtered = openOnly
                ? stories.filter((s: any) => !CLOSED_STATUSES.has(String(s.status || "").toLowerCase()))
                : stories;

            return {
                project_id: projectId,
                count: filtered.length,
                openOnly,
                stories: filtered.slice(0, MAX_STORIES).map((s: any) => ({
                    id: s.id,
                    story_number: s.story_number,
                    title: s.title,
                    status: s.status,
                    priority: s.priority,
                    assigned_to: s.assigned_to
                })),
                truncated: filtered.length > MAX_STORIES
            };
        }
    });

    const list_features = defineChatTool<
        { projectId?: number; projectName?: string },
        AppChatEventMap
    >({
        name: "list_features",
        description:
            "Returns all features for a single product. Pass projectId OR projectName. " +
            "If neither is passed, this tool will use the currently open product context when available. " +
            "If no current product context exists, call list_projects and ask the user which one they mean. " +
            "Returns feature title and description only. " +
            "IMPORTANT: When presenting features to users, show them as a simple list with title and description only. " +
            "DO NOT include ID numbers, status, goals, or success metrics.",
        category: "read",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number", description: "Numeric project id from list_projects." },
                projectName: { type: "string", description: "Project name. Used only if projectId not provided." }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args) => {
            const token = await getToken();

            const projects = await fetchProjects(token);
            const resolved = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolved.target) {
                return {
                    error: resolved.error,
                    ...(resolved.matches ? { matches: resolved.matches } : {})
                };
            }
            const projectId = resolved.target.project_id;

            try {
                const features = await fetchProjectFeaturesApi(projectId, token);

                return {
                    project_id: projectId,
                    count: features.length,
                    features: features.map((f: any) => ({
                        title: f.title,
                        description: f.description
                    }))
                };
            } catch (error: any) {
                return {
                    error: `Failed to fetch features: ${error.message}`
                };
            }
        }
    });

    const navigate_to_project = defineChatTool<
        { projectId?: number; projectName?: string },
        AppChatEventMap
    >({
        name: "navigate_to_project",
        category: "mutation",
        description:
            "Opens a product in the main app UI. Pass projectId OR projectName. " +
            "Use this when the user says things like 'show me X', 'open X', 'take me to X', 'switch to X'. " +
            "When the user names a product, pass projectName only; do not pair it with an id remembered from an earlier product. " +
            "If no name matches, the result includes closestMatch and onConfirm. Do not navigate automatically; ask the returned confirmationPrompt and wait. " +
            "When the user confirms, execute onConfirm by calling navigate_to_project with its projectId. " +
            "If the product name is ambiguous (multiple matches), DO NOT navigate — ask the user which one. " +
            "On success, the UI navigates to the product's Feature Studio view.",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number" },
                projectName: { type: "string" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const token = await getToken();
            const projects = await fetchProjects(token);
            const resolved = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolved.target) {
                const confirmationPrompt = resolved.closestMatch?.name
                    ? `Did you mean "${resolved.closestMatch.name}"? Would you like me to open it?`
                    : undefined;
                return {
                    error: resolved.error,
                    ...(resolved.matches ? { matches: resolved.matches } : {}),
                    ...(resolved.closestMatch
                        ? {
                              closestMatch: resolved.closestMatch,
                              confirmationRequired: true,
                              confirmationPrompt,
                              onConfirm: {
                                  tool: "navigate_to_project",
                                  arguments: { projectId: resolved.closestMatch.id }
                              }
                          }
                        : {})
                };
            }
            const target = resolved.target;

            ctx.emit("navigate_to_project", {
                projectId: target.project_id,
                projectName: target.project_name ?? `Product ${target.project_id}`
            });

            return {
                ok: true,
                projectId: target.project_id,
                projectName: target.project_name,
                message: `Navigating to ${target.project_name}.`
            };
        }
    });

    const navigate_to_feature = defineChatTool<
        { projectId?: number; projectName?: string; featureId?: number; featureName?: string },
        AppChatEventMap
    >({
        name: "navigate_to_feature",
        category: "mutation",
        description:
            "Opens a specific feature in the main app UI and mirrors a manual feature click " +
            "(expand/select/focus). Pass featureId OR featureName, and optionally projectId/projectName. " +
            "If product context is already open, project args are optional. " +
            "This is a terminal single action: it only navigates. Do NOT chain follow-up actions " +
            "(opening work products, generating content, activating UI elements) from earlier turns after calling it; " +
            "additional actions requested in the same user message are fine. " +
            "Always call this tool when the user asks to open/switch to this target, even if it may already be the " +
            "current one — re-issuing the same navigation is safe; never skip it with a text-only reply.",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number" },
                projectName: { type: "string" },
                featureId: { type: "number" },
                featureName: { type: "string" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const requestedFeatureId = normalizePositiveId(args.featureId);
            if (requestedFeatureId == null && !args.featureName) {
                return {
                    error: "Feature is required. Provide featureId or featureName."
                };
            }

            const token = await getToken();
            const projects = await fetchProjects(token);
            const resolvedProject = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolvedProject.target) {
                return {
                    error: resolvedProject.error,
                    ...(resolvedProject.matches ? { matches: resolvedProject.matches } : {})
                };
            }

            const targetProject = resolvedProject.target;
            const features = (await fetchProjectFeaturesApi(targetProject.project_id, token)) as unknown as FeatureRecord[];

            let targetFeature: FeatureRecord | undefined;
            if (requestedFeatureId != null) {
                targetFeature = features.find((f) => featureIdOf(f) === requestedFeatureId);
            }
            if (!targetFeature && args.featureName) {
                const matches = matchFeaturesByName(features, args.featureName);
                if (matches.length > 1) {
                    return {
                        error: `Multiple features matched "${args.featureName}". Ask the user which one.`,
                        matches: matches.map((f) => ({ id: featureIdOf(f), name: featureNameOf(f) }))
                    };
                }
                targetFeature = matches[0];
            }

            const resolvedFeatureId = targetFeature ? featureIdOf(targetFeature) : null;
            if (!targetFeature || resolvedFeatureId == null) {
                return {
                    error: "Feature not found in the target product. Call list_features and ask the user which one they meant."
                };
            }

            const resolvedFeatureName = featureNameOf(targetFeature);
            ctx.emit("navigate_to_feature", {
                projectId: targetProject.project_id,
                projectName: targetProject.project_name ?? `Product ${targetProject.project_id}`,
                featureId: resolvedFeatureId,
                featureName: resolvedFeatureName
            });

            return {
                ok: true,
                projectId: targetProject.project_id,
                projectName: targetProject.project_name,
                featureId: resolvedFeatureId,
                featureName: resolvedFeatureName,
                message: `Navigating to feature ${resolvedFeatureName}.`
            };
        }
    });

    const navigate_to_user_story = defineChatTool<
        {
            projectId?: number;
            projectName?: string;
            featureId?: number;
            featureName?: string;
            userStoryId?: string;
            storyNumber?: string;
            storyTitle?: string;
        },
        AppChatEventMap
    >({
        name: "navigate_to_user_story",
        category: "mutation",
        description:
            "Opens a specific user story in the main app UI and mirrors a manual user-story click " +
            "(expand parent feature, select story, focus row). Provide one story selector: userStoryId, storyNumber, or storyTitle. " +
            "Optionally scope by featureId/featureName and projectId/projectName. " +
            "This is a terminal single action: it only navigates. Do NOT chain follow-up actions " +
            "(opening work products, generating content, activating UI elements) from earlier turns after calling it; " +
            "additional actions requested in the same user message are fine. " +
            "Always call this tool when the user asks to open/switch to this target, even if it may already be the " +
            "current one — re-issuing the same navigation is safe; never skip it with a text-only reply.",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number" },
                projectName: { type: "string" },
                featureId: { type: "number" },
                featureName: { type: "string" },
                userStoryId: { type: "string" },
                storyNumber: { type: "string" },
                storyTitle: { type: "string" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            if (!args.userStoryId && !args.storyNumber && !args.storyTitle) {
                return {
                    error: "User story is required. Provide userStoryId, storyNumber, or storyTitle."
                };
            }

            const token = await getToken();
            const projects = await fetchProjects(token);
            const resolvedProject = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolvedProject.target) {
                return {
                    error: resolvedProject.error,
                    ...(resolvedProject.matches ? { matches: resolvedProject.matches } : {})
                };
            }

            const targetProject = resolvedProject.target;
            const features = (await fetchProjectFeaturesApi(targetProject.project_id, token)) as unknown as FeatureRecord[];

            let scopedFeature: FeatureRecord | undefined;
            const requestedFeatureId = normalizePositiveId(args.featureId);
            if (requestedFeatureId != null) {
                scopedFeature = features.find((f) => featureIdOf(f) === requestedFeatureId);
                if (!scopedFeature) {
                    return {
                        error: `Feature ${requestedFeatureId} was not found in ${targetProject.project_name}.`
                    };
                }
            } else if (args.featureName) {
                const featureMatches = matchFeaturesByName(features, args.featureName);
                if (featureMatches.length > 1) {
                    return {
                        error: `Multiple features matched "${args.featureName}". Ask the user which one.`,
                        matches: featureMatches.map((f) => ({ id: featureIdOf(f), name: featureNameOf(f) }))
                    };
                }
                scopedFeature = featureMatches[0];
                if (!scopedFeature) {
                    return {
                        error: `No feature matched "${args.featureName}" in ${targetProject.project_name}.`
                    };
                }
            }

            const allStories = (await getUserStoriesByProject(
                targetProject.project_id,
                token,
                false,
                true
            )) as unknown as UserStoryRecord[];

            const scopedFeatureId = scopedFeature ? featureIdOf(scopedFeature) : null;
            const stories = scopedFeatureId != null
                ? allStories.filter((s) => Number(s.feature_id) === scopedFeatureId)
                : allStories;

            let storyMatches = stories;
            if (args.userStoryId) {
                const requestedId = normalizeText(args.userStoryId);
                storyMatches = storyMatches.filter((s) => normalizeText(storyIdOf(s)) === requestedId);
            }
            if (args.storyNumber) {
                const requestedStoryNumber = normalizeText(args.storyNumber);
                storyMatches = storyMatches.filter((s) => normalizeText(storyNumberOf(s)) === requestedStoryNumber);
            }
            if (args.storyTitle) {
                storyMatches = matchStoriesByTitle(storyMatches, args.storyTitle);
            }

            if (storyMatches.length === 0) {
                return {
                    error: "User story not found in the target scope. Call list_user_stories and ask the user which one they meant."
                };
            }
            if (storyMatches.length > 1) {
                return {
                    error: "Multiple user stories matched. Ask the user which one.",
                    matches: storyMatches.slice(0, 10).map((s) => ({
                        id: storyIdOf(s),
                        story_number: storyNumberOf(s),
                        title: storyTitleOf(s)
                    }))
                };
            }

            const targetStory = storyMatches[0];
            const targetFeatureId = Number(targetStory.feature_id);
            const targetFeature = features.find((f) => featureIdOf(f) === targetFeatureId);
            if (!targetFeature || !Number.isFinite(targetFeatureId)) {
                return {
                    error: `Found story "${storyTitleOf(targetStory)}" but its feature could not be resolved.`
                };
            }

            const resolvedFeatureName = featureNameOf(targetFeature);
            const resolvedStoryId = storyIdOf(targetStory);
            const resolvedStoryNumber = storyNumberOf(targetStory);
            const resolvedStoryTitle = storyTitleOf(targetStory) || `User Story ${resolvedStoryId}`;

            ctx.emit("navigate_to_user_story", {
                projectId: targetProject.project_id,
                projectName: targetProject.project_name ?? `Product ${targetProject.project_id}`,
                featureId: targetFeatureId,
                featureName: resolvedFeatureName,
                userStoryId: resolvedStoryId,
                storyNumber: resolvedStoryNumber || undefined,
                storyTitle: resolvedStoryTitle
            });

            return {
                ok: true,
                projectId: targetProject.project_id,
                projectName: targetProject.project_name,
                featureId: targetFeatureId,
                featureName: resolvedFeatureName,
                userStoryId: resolvedStoryId,
                storyNumber: resolvedStoryNumber || null,
                storyTitle: resolvedStoryTitle,
                message: `Navigating to user story ${resolvedStoryNumber || resolvedStoryTitle}.`
            };
        }
    });

    const navigate_to_hierarchy_item = defineChatTool<
        { itemName: string; projectId?: number; projectName?: string },
        AppChatEventMap
    >({
        name: "navigate_to_hierarchy_item",
        category: "mutation",
        description:
            "Opens a feature or user story when the user names the item without saying which type it is. " +
            "Use this for unqualified requests such as 'Choose Habit-Aware Reminder Personalization'. " +
            "It resolves the name against both features and user stories in the current product and asks for clarification only when ambiguous.",
        parameters: {
            type: "object",
            properties: {
                itemName: { type: "string", description: "Feature name or user-story title exactly as the user supplied it." },
                projectId: { type: "number" },
                projectName: { type: "string" }
            },
            required: ["itemName"],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const token = await getToken();
            const projects = await fetchProjects(token);
            const resolvedProject = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolvedProject.target) {
                return {
                    error: resolvedProject.error,
                    ...(resolvedProject.matches ? { matches: resolvedProject.matches } : {})
                };
            }

            const targetProject = resolvedProject.target;
            const [features, stories] = await Promise.all([
                fetchProjectFeaturesApi(targetProject.project_id, token) as unknown as Promise<FeatureRecord[]>,
                getUserStoriesByProject(targetProject.project_id, token, false, true) as unknown as Promise<UserStoryRecord[]>
            ]);
            const featureMatches = matchFeaturesByName(features, args.itemName);
            const storyMatches = matchStoriesByTitle(stories, args.itemName);
            const matchCount = featureMatches.length + storyMatches.length;

            if (matchCount === 0) {
                return {
                    error: `No feature or user story matched "${args.itemName}" in ${targetProject.project_name}.`,
                    suggestion: "Call list_features and list_user_stories, then ask the user which item they meant."
                };
            }
            if (matchCount > 1) {
                return {
                    error: `Multiple features or user stories matched "${args.itemName}". Ask the user which one they meant.`,
                    matches: [
                        ...featureMatches.map((feature) => ({ type: "feature", name: featureNameOf(feature) })),
                        ...storyMatches.map((story) => ({
                            type: "user story",
                            name: storyTitleOf(story),
                            story_number: storyNumberOf(story) || undefined
                        }))
                    ].slice(0, 10)
                };
            }

            if (featureMatches.length === 1) {
                const targetFeature = featureMatches[0];
                const featureId = featureIdOf(targetFeature);
                if (featureId == null) return { error: `Feature "${featureNameOf(targetFeature)}" has no valid id.` };
                const featureName = featureNameOf(targetFeature);
                ctx.emit("navigate_to_feature", {
                    projectId: targetProject.project_id,
                    projectName: targetProject.project_name ?? `Product ${targetProject.project_id}`,
                    featureId,
                    featureName
                });
                return { ok: true, entityType: "feature", featureId, featureName };
            }

            const targetStory = storyMatches[0];
            const featureId = Number(targetStory.feature_id);
            const targetFeature = features.find((feature) => featureIdOf(feature) === featureId);
            if (!targetFeature || !Number.isFinite(featureId)) {
                return { error: `Found story "${storyTitleOf(targetStory)}" but its feature could not be resolved.` };
            }
            const storyTitle = storyTitleOf(targetStory) || `User Story ${storyIdOf(targetStory)}`;
            const storyNumber = storyNumberOf(targetStory);
            ctx.emit("navigate_to_user_story", {
                projectId: targetProject.project_id,
                projectName: targetProject.project_name ?? `Product ${targetProject.project_id}`,
                featureId,
                featureName: featureNameOf(targetFeature),
                userStoryId: storyIdOf(targetStory),
                storyNumber: storyNumber || undefined,
                storyTitle
            });
            return { ok: true, entityType: "user_story", userStoryId: storyIdOf(targetStory), storyTitle };
        }
    });

    const open_add_feature = defineChatTool<
        { projectId?: number; projectName?: string },
        AppChatEventMap
    >({
        name: "open_add_feature",
        category: "mutation",
        description:
            "Opens the existing Add Feature form for a product without requiring the hidden hierarchy sidebar. " +
            "Use the current product when projectId/projectName are omitted. This opens the form; it does not claim a feature was created.",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number" },
                projectName: { type: "string" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const token = await getToken();
            const projects = await fetchProjects(token);
            const resolvedProject = resolveProjectTarget(projects, args);
            if (!resolvedProject.target) {
                return {
                    error: resolvedProject.error,
                    ...(resolvedProject.matches ? { matches: resolvedProject.matches } : {})
                };
            }

            const target = resolvedProject.target;
            const projectName = target.project_name ?? `Product ${target.project_id}`;
            ctx.emit("open_add_feature", { projectId: target.project_id, projectName });
            return {
                ok: true,
                projectId: target.project_id,
                projectName,
                message: `Opening the Add Feature form for ${projectName}.`
            };
        }
    });

    const open_add_user_story = defineChatTool<
        { projectId?: number; projectName?: string; featureId?: number; featureName?: string },
        AppChatEventMap
    >({
        name: "open_add_user_story",
        category: "mutation",
        description:
            "Opens the existing Add User Story form for a feature without requiring the hidden hierarchy sidebar. " +
            "Uses the current feature when featureId/featureName are omitted. This opens the form; it does not claim a story was created.",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number" },
                projectName: { type: "string" },
                featureId: { type: "number" },
                featureName: { type: "string" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const token = await getToken();
            const projects = await fetchProjects(token);
            const resolvedProject = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolvedProject.target) {
                return {
                    error: resolvedProject.error,
                    ...(resolvedProject.matches ? { matches: resolvedProject.matches } : {})
                };
            }

            const targetProject = resolvedProject.target;
            const currentHierarchy = getCurrentHierarchySnapshot();
            const currentHierarchyMatchesProject =
                currentHierarchy?.projectId != null
                    ? Number(currentHierarchy.projectId) === targetProject.project_id
                    : Boolean(currentHierarchy?.projectName) &&
                      normalizeText(currentHierarchy?.projectName) === normalizeText(targetProject.project_name);
            const currentFeatureId =
                currentHierarchyMatchesProject && currentHierarchy?.hierarchyLevel !== "product" && currentHierarchy?.featureId != null
                    ? normalizePositiveId(currentHierarchy.featureId)
                    : null;
            const requestedFeatureId = normalizePositiveId(args.featureId) ?? currentFeatureId;
            const requestedFeatureName =
                args.featureName ||
                (currentHierarchyMatchesProject && currentHierarchy?.hierarchyLevel !== "product"
                    ? currentHierarchy?.featureName ?? undefined
                    : undefined);

            if (requestedFeatureId == null && !requestedFeatureName) {
                return {
                    error: "Feature is required. Open a feature first or provide featureId/featureName."
                };
            }

            const features = (await fetchProjectFeaturesApi(targetProject.project_id, token)) as unknown as FeatureRecord[];
            let targetFeature: FeatureRecord | undefined;
            if (requestedFeatureId != null) {
                targetFeature = features.find(feature => featureIdOf(feature) === requestedFeatureId);
            }
            if (!targetFeature && requestedFeatureName) {
                const matches = matchFeaturesByName(features, requestedFeatureName);
                if (matches.length > 1) {
                    return {
                        error: `Multiple features matched "${requestedFeatureName}". Ask the user which one.`,
                        matches: matches.map(feature => ({ id: featureIdOf(feature), name: featureNameOf(feature) }))
                    };
                }
                targetFeature = matches[0];
            }

            const featureId = targetFeature ? featureIdOf(targetFeature) : null;
            if (!targetFeature || featureId == null) {
                return {
                    error: "Feature not found in the target product. Call list_features and ask the user which one they meant."
                };
            }

            const projectName = targetProject.project_name ?? `Product ${targetProject.project_id}`;
            const featureName = featureNameOf(targetFeature);
            ctx.emit("open_add_user_story", {
                projectId: targetProject.project_id,
                projectName,
                featureId,
                featureName
            });
            return {
                ok: true,
                projectId: targetProject.project_id,
                projectName,
                featureId,
                featureName,
                message: `Opening the Add User Story form for ${featureName}.`
            };
        }
    });

    const navigate_to_feature_studio = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "navigate_to_feature_studio",
        category: "mutation",
        description:
            "Returns from the current editor document back to Feature Studio, " +
            "equivalent to clicking the top-left back button in the editor. " +
            "Use for requests like 'go back to feature studio' or 'return to feature studio'.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (_args, ctx) => {
            ctx.emit("navigate_to_feature_studio", {});
            return {
                ok: true,
                message: "Navigating back to Feature Studio."
            };
        }
    });

    const createSddPageNavigationTool = (
        name: "navigate_to_sdd_product_hierarchy" | "navigate_to_sdd_scope_picker" | "navigate_to_sdd_progress_report",
        description: string,
        message: string
    ) =>
        defineChatTool<Record<string, never>, AppChatEventMap>({
            name,
            category: "mutation",
            description,
            parameters: { type: "object", properties: {}, required: [] as string[], additionalProperties: false },
            execute: async (_args, ctx) => {
                ctx.emit(name, {});
                return { ok: true, message };
            }
        });

    const navigate_to_sdd_product_hierarchy = createSddPageNavigationTool(
        "navigate_to_sdd_product_hierarchy",
        "Opens the full Product / Feature / Story hierarchy page in Feature Studio from any FDE screen.",
        "Opening the Product / Feature / Story hierarchy."
    );
    const navigate_to_sdd_scope_picker = createSddPageNavigationTool(
        "navigate_to_sdd_scope_picker",
        "Opens the Select Feature & Story page in Feature Studio from any FDE screen.",
        "Opening the Select Feature & Story page."
    );
    const navigate_to_sdd_progress_report = createSddPageNavigationTool(
        "navigate_to_sdd_progress_report",
        "Opens the SDD progress details report in Feature Studio from any FDE screen.",
        "Opening SDD progress details."
    );

    const navigate_to_sdd_stage = defineChatTool<
        { stage: "specify" | "plan" | "task_breakdown" | "implement" | "validate" },
        AppChatEventMap
    >({
        name: "navigate_to_sdd_stage",
        category: "mutation",
        description: "Switches Feature Studio directly to a named SDD stage: Specify, Plan, Define Tasks, Implement, or Validate.",
        parameters: {
            type: "object",
            properties: {
                stage: {
                    type: "string",
                    enum: ["specify", "plan", "task_breakdown", "implement", "validate"],
                    description: "Use task_breakdown for the Define Tasks stage."
                }
            },
            required: ["stage"],
            additionalProperties: false
        },
        execute: async ({ stage }, ctx) => {
            ctx.emit("navigate_to_sdd_stage", { stage });
            return { ok: true, stage, message: `Opening the ${stage === "task_breakdown" ? "Define Tasks" : stage} stage.` };
        }
    });

    const navigate_to_new_product = defineChatTool<{ product_mode?: ProductKind }, AppChatEventMap>({
        name: "navigate_to_new_product",
        category: "mutation",
        description:
            "Navigates to the standalone new-product screen (/products/new). " +
            "Use when the user asks to create or start a brand-new product. " +
            "Pass product_mode when the user's intent already answers the New-vs-Existing question " +
            "so the form opens with Product Type pre-answered and unlocked.",
        parameters: {
            type: "object",
            properties: {
                product_mode: {
                    type: "string",
                    enum: ["new", "existing"],
                    description: "Pre-answer for the Product Type gate (New vs Existing product)."
                }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const product_mode = args.product_mode === "new" || args.product_mode === "existing" ? args.product_mode : undefined;
            ctx.emit("navigate_to_new_product", product_mode ? { product_mode } : {});
            return {
                ok: true,
                message: product_mode
                    ? `Navigating to the standalone new-product screen with Product Type pre-answered as "${product_mode}".`
                    : "Navigating to the standalone new-product screen."
            };
        }
    });

    const navigate_to_new_context_mesh = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "navigate_to_new_context_mesh",
        category: "mutation",
        description:
            "Navigates directly to the standalone Context Mesh creation screen (/context-meshes/new). " +
            "Call this immediately when the user asks to create, add, or build Context.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (_args, ctx) => {
            ctx.emit("navigate_to_new_context_mesh", {});
            return {
                ok: true,
                message:
                    "Opening Create a Context Mesh. If the user requested form values, preserve that request, read the newly mounted draft, and continue the same task without a welcome or another discovery question."
            };
        }
    });

    const list_context_meshes = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "list_context_meshes",
        category: "read",
        description:
            "Lists the standalone Context Meshes available to the current user. Call this when the user asks " +
            "what Context Meshes are available, or asks to list, show, or view their Context Meshes.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async () => {
            const token = await getToken();
            if (!token) return { ok: false, error: "Authentication is required to list Context Meshes." };
            try {
                const meshes = await listContextMeshes(token);
                return {
                    ok: true,
                    count: meshes.length,
                    contextMeshes: meshes.map(mesh => ({
                        contextMeshId: mesh.contextMeshId,
                        name: mesh.name,
                        description: mesh.description,
                        status: mesh.lifecycleStatus,
                        updatedAt: mesh.updatedAt,
                        score: mesh.analysisSummary?.score ?? null,
                        analysisStatus: mesh.analysisSummary?.status ?? null
                    })),
                    message: meshes.length === 0 ? "No Context Meshes are available." : undefined
                };
            } catch (error) {
                return { ok: false, error: error instanceof Error ? error.message : "Unable to load Context Meshes." };
            }
        }
    });

    const get_context_mesh_sources = defineChatTool<{ contextMeshId?: string; contextMeshName?: string }, AppChatEventMap>({
        name: "get_context_mesh_sources",
        category: "read",
        description:
            "Returns the files, repositories, and external integrations linked to a specific standalone Context Mesh. " +
            "Use this when the user asks which Jira project, repository, file, or other source is connected to a Context Mesh, " +
            "or for its key/branch, status, last-sync time, error, or synced ticket/work-item/file count. " +
            "The returned identity and sync fields are authoritative for the Integrations table.",
        parameters: {
            type: "object",
            properties: {
                contextMeshId: { type: "string", description: "Context Mesh id returned by list_context_meshes." },
                contextMeshName: { type: "string", description: "Context Mesh name supplied by the user." }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async ({ contextMeshId, contextMeshName }) => {
            const token = await getToken();
            if (!token) return { ok: false, error: "Authentication is required to inspect Context Mesh sources." };
            try {
                let meshId = contextMeshId?.trim();
                let meshName = contextMeshName?.trim();
                if (!meshId) {
                    if (!meshName) return { ok: false, error: "Provide a Context Mesh name or id." };
                    const mesh = resolveContextMeshByName(await listContextMeshes(token), meshName);
                    if (!mesh) return { ok: false, error: `No unique Context Mesh matched "${meshName}".` };
                    meshId = mesh.contextMeshId;
                    meshName = mesh.name;
                }
                const sources = await listContextMeshSources(token, meshId);
                const integrationLinksById = new Map<string, Awaited<ReturnType<typeof fetchIntegrationLinks>>["vendors"][number]["links"][number]>();
                if (sources.some(source => typeof source.syncedCount !== "number")) {
                    try {
                        const listing = await fetchIntegrationLinks(token);
                        for (const vendor of listing.vendors || []) {
                            for (const link of vendor.links || []) {
                                if (link.target?.type === "context_mesh" && String(link.target.id) === meshId) {
                                    integrationLinksById.set(String(link.id), link);
                                }
                            }
                        }
                    } catch {
                        // Source identity remains useful when the UI link listing is temporarily unavailable.
                    }
                }
                return {
                    ok: true,
                    contextMeshId: meshId,
                    contextMeshName: meshName,
                    count: sources.length,
                    sources: sources.map(source => {
                        const uiLink = integrationLinksById.get(String(source.sourceBindingId));
                        return {
                        kind: source.sourceKind,
                        provider: source.provider,
                        name: source.sourceName ?? source.externalName ?? source.repositoryName ?? source.fileName ?? uiLink?.externalName ?? null,
                        reference: source.sourceReference ?? source.externalRef ?? source.repositoryUrl ?? uiLink?.externalRef ?? null,
                        branch: source.branch ?? (typeof uiLink?.metadata?.branch === "string" ? uiLink.metadata.branch : null),
                        syncStatus: uiLink?.syncStatus ?? source.lastSyncStatus,
                        lastSyncedAt: uiLink?.lastSyncedAt ?? source.lastSyncedAt,
                        syncError: uiLink?.lastSyncError ?? source.lastErrorCode ?? null,
                        itemCount: typeof uiLink?.syncedCount === "number"
                            ? uiLink.syncedCount
                            : typeof source.syncedCount === "number"
                            ? source.syncedCount
                            : typeof source.configuration?.importedCount === "number"
                                ? source.configuration.importedCount
                                : typeof source.integrationMetadata?.importedCount === "number"
                                    ? source.integrationMetadata.importedCount
                                    : null,
                        itemUnit: uiLink?.syncedUnit ?? source.syncedUnit ?? null
                    }}),
                    message: sources.length === 0 ? "No sources are linked to this Context Mesh." : undefined
                };
            } catch (error) {
                return { ok: false, error: error instanceof Error ? error.message : "Unable to load Context Mesh sources." };
            }
        }
    });

    const navigate_to_context_mesh = defineChatTool<{ contextMeshId?: string; contextMeshName?: string }, AppChatEventMap>({
        name: "navigate_to_context_mesh",
        category: "mutation",
        description:
            "Opens a standalone Context Mesh analysis page. Use when the user asks to open, view, select, or go to " +
            "one of their Context Meshes. Pass the name exactly as the user supplied it or the id returned by list_context_meshes.",
        parameters: {
            type: "object",
            properties: {
                contextMeshId: { type: "string", description: "Context Mesh id returned by list_context_meshes." },
                contextMeshName: { type: "string", description: "Context Mesh name supplied by the user." }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async ({ contextMeshId, contextMeshName }, ctx) => {
            const requestedId = contextMeshId?.trim();
            const requestedName = contextMeshName?.trim();
            if (!requestedId && !requestedName) {
                return { ok: false, error: "Provide a Context Mesh name or id. Call list_context_meshes to see what is available." };
            }
            const token = await getToken();
            if (!token) return { ok: false, error: "Authentication is required to open a Context Mesh." };
            try {
                const meshes = await listContextMeshes(token);
                let match = requestedId ? meshes.find(mesh => mesh.contextMeshId === requestedId) : undefined;
                if (!match && requestedName) {
                    const normalizedName = requestedName.toLocaleLowerCase();
                    match = resolveContextMeshByName(meshes, requestedName) ?? undefined;
                    if (!match) {
                        const partialMatches = meshes.filter(mesh => mesh.name.toLocaleLowerCase().includes(normalizedName));
                        if (partialMatches.length > 1) {
                            return {
                                ok: false,
                                ambiguous: true,
                                error: `More than one Context Mesh matches "${requestedName}".`,
                                matches: partialMatches.map(mesh => ({ contextMeshId: mesh.contextMeshId, name: mesh.name }))
                            };
                        }
                    }
                }
                if (!match) {
                    return {
                        ok: false,
                        error: `No Context Mesh matched "${requestedName || requestedId}".`,
                        availableContextMeshes: meshes.map(mesh => ({ contextMeshId: mesh.contextMeshId, name: mesh.name }))
                    };
                }
                ctx.emit("navigate_to_context_mesh", {
                    contextMeshId: match.contextMeshId,
                    contextMeshName: match.name
                });
                return {
                    ok: true,
                    contextMeshId: match.contextMeshId,
                    contextMeshName: match.name,
                    message: `Opening the Context Mesh analysis for ${match.name}.`
                };
            } catch (error) {
                return { ok: false, error: error instanceof Error ? error.message : "Unable to open the Context Mesh." };
            }
        }
    });

    const navigate_to_context_mesh_hub = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "navigate_to_context_mesh_hub",
        category: "mutation",
        description: "Opens the standalone Context Mesh Hub (/context-meshes). Call immediately when the user asks to go, navigate, or open the Context Mesh Hub.",
        parameters: { type: "object", properties: {}, required: [] as string[], additionalProperties: false },
        execute: async (_args, ctx) => {
            ctx.emit("navigate_to_context_mesh_hub", {});
            return { ok: true, message: "Opening Context Mesh Hub." };
        }
    });

    const navigate_to_integrations = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "navigate_to_integrations",
        category: "mutation",
        description:
            "Navigates to the existing Integrations page. When the user directly asks to open or navigate to " +
            "Integrations, call this tool immediately without asking a follow-up confirmation question. Never use this tool when the user asks " +
            "to disconnect, remove, unlink, or revoke Azure DevOps or Jira; use integration_disconnect instead so the " +
            "required confirmation card is shown.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (_args, ctx) => {
            ctx.emit("navigate_to_integrations", {});
            return {
                ok: true,
                message: "Navigating to Integrations."
            };
        }
    });

    const navigate_to_files = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "navigate_to_files",
        // Opening an existing view is read-only. Keeping this out of the
        // mutation category prevents CodeNexus from requesting approval for
        // a harmless Files-page navigation (including when already there).
        category: "read",
        description:
            "Opens Files & Attachments, displaying the complete unfiltered artifact list and every file's linked " +
            "product or products. Use whenever the user asks to list or show all uploaded files/artifacts with " +
            "linked products. When the user directly asks to open or navigate to the Files page, call this tool " +
            "immediately without asking a follow-up confirmation question. This is a terminal UI action: after success, " +
            "end the turn without assistant prose. " +
            "Never say the complete mapping cannot be retrieved and never ask the user to choose another action.",
        parameters: { type: "object", properties: {}, required: [] as string[], additionalProperties: false },
        execute: async (_args, ctx) => {
            ctx.emit("navigate_to_files", {});
            return { ok: true, message: "Displaying all files and their linked products." };
        }
    });

    const list_uploaded_files_with_products = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "list_uploaded_files_with_products",
        category: "read",
        description:
            "Returns every uploaded file and its linked product names, and opens the authoritative unfiltered " +
            "Files & Attachments view. Use this—not navigate_to_files—when the user asks to list or name uploaded " +
            "files/artifacts in chat, especially with linked products. After success, print every returned file " +
            "and its linkedProducts in chat. If empty is true, clearly say no files are uploaded. If ok is false, " +
            "report the actionable error. Do not replace the result with navigation commentary or follow-up choices.",
        parameters: { type: "object", properties: {}, required: [] as string[], additionalProperties: false },
        execute: async (_args, ctx) => {
            const result = await loadUploadedFilesWithProducts(getToken, userId);
            if (!result.ok) return result;
            ctx.emit("navigate_to_files", {});
            const files = result.files ?? [];
            return files.length === 0
                ? { ok: true, empty: true, count: 0, files: [] }
                : { ok: true, empty: false, count: files.length, files };
        }
    });

    const assign_uploaded_file_to_product = defineChatTool<{
        file_name: string;
        product_name: string;
    }, AppChatEventMap>({
        name: "assign_uploaded_file_to_product",
        category: "mutation",
        description:
            "Assigns one existing Files & Attachments file to one named product using the same scope update as the " +
            "Assign action. Resolves both names without row selection, returns candidates for ambiguous names, reports " +
            "missing targets or an existing assignment, and verifies the saved assignment before reporting success.",
        parameters: {
            type: "object",
            properties: {
                file_name: { type: "string", description: "Exact or uniquely identifying uploaded filename." },
                product_name: { type: "string", description: "Exact or uniquely identifying product name." }
            },
            required: ["file_name", "product_name"],
            additionalProperties: false
        },
        execute: async ({ file_name, product_name }, ctx) => {
            if (!userId) return { ok: false, reason: "profile_unavailable", error: "Your user profile is not available. Refresh the page and try again." };
            try {
                const token = await getToken();
                const filesResponse = await fetchWithAuth(
                    `${BACKEND_URI}/api/vector_files/get_all_user_files/${userId}`,
                    token,
                    { method: "GET" }
                );
                if (!filesResponse.ok) return { ok: false, reason: "files_unavailable", error: "Files could not be loaded. Check your connection and try again." };
                const filesPayload = await filesResponse.json();
                const files = Array.isArray(filesPayload?.files) ? filesPayload.files : [];
                const fileResolution = resolveAssignmentFile(files, file_name);
                if (!fileResolution.ok || !fileResolution.target) return fileResolution;

                const productResolution = resolveAssignmentProduct(await fetchProjects(token), product_name);
                if (!productResolution.ok || !productResolution.target) return productResolution;

                const file = fileResolution.target;
                const product = productResolution.target;
                const scope = await fetchFileScope(token, file.fileid);
                if (!scope.global && scope.projects.some(item => item.project_id === product.project_id)) {
                    return {
                        ok: true,
                        already_attached: true,
                        file: file.filename,
                        product: product.project_name,
                        message: `${file.filename} is already assigned to ${product.project_name}.`
                    };
                }

                const projectIds = scope.global
                    ? [product.project_id]
                    : [...new Set([...scope.projects.map(item => item.project_id), product.project_id])];
                const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/update_file_scopes`, token, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        fileid: file.fileid,
                        is_global: false,
                        company_id: null,
                        project_ids: projectIds,
                        feature_ids: [],
                        changes: {
                            added_project_ids: [product.project_id],
                            removed_project_ids: [],
                            added_feature_ids: [],
                            removed_feature_ids: scope.features.map(item => item.feature_id)
                        }
                    })
                });
                if (!response.ok) {
                    const payload = await response.json().catch(() => null);
                    return { ok: false, reason: "assignment_failed", error: payload?.error ?? "The file could not be assigned. Try again." };
                }

                const verifiedScope = await fetchFileScope(token, file.fileid);
                const verified = !verifiedScope.global && verifiedScope.projects.some(item => item.project_id === product.project_id);
                if (!verified) return { ok: false, reason: "verification_failed", error: "The assignment was saved but could not be verified. Refresh Files & Attachments and try again." };
                ctx.emit("uploaded_file_assignment_changed", {
                    fileId: file.fileid,
                    filename: file.filename,
                    projectId: product.project_id,
                    projectName: product.project_name ?? product_name
                });
                return {
                    ok: true,
                    already_attached: false,
                    verified: true,
                    file: file.filename,
                    product: product.project_name,
                    message: `${file.filename} was assigned to ${product.project_name}.`
                };
            } catch (error) {
                return { ok: false, reason: "assignment_failed", error: error instanceof Error ? error.message : "The file could not be assigned. Try again." };
            }
        }
    });

    const resolve_uploaded_file_for_deletion = defineChatTool<{ path: string }, AppChatEventMap>({
        name: "resolve_uploaded_file_for_deletion",
        category: "read",
        description:
            "Resolves an explicit uploaded-file deletion request against the authenticated user's Files & Attachments " +
            "inventory. Call this before delete_uploaded_file. It returns one exact target, candidate files requiring " +
            "clarification, not_found, or unauthorized_path. Never guess a file id or delete an ambiguous match.",
        parameters: {
            type: "object",
            properties: {
                path: { type: "string", description: "The filename or artifact description provided by the user." }
            },
            required: ["path"],
            additionalProperties: false
        },
        execute: async ({ path }) => {
            resolvedDeletionTargets.clear();
            if (isUnauthorizedFilePath(path)) return resolveUploadedFileTarget([], path);
            const result = await loadUploadedFilesWithProducts(getToken, userId);
            if (!result.ok) return result;
            const resolution = resolveUploadedFileTarget(result.files ?? [], path);
            if (resolution.ok && resolution.target) {
                resolvedDeletionTargets.set(resolution.target.fileId, resolution.target);
            }
            return resolution;
        }
    });

    const delete_uploaded_file = Object.assign(
        defineChatTool<{
            file_id: number;
            filename: string;
            linked_products: string[];
        }, AppChatEventMap>({
            name: "delete_uploaded_file",
            category: "mutation",
            description:
                "Permanently deletes exactly one uploaded file after resolve_uploaded_file_for_deletion returned it. " +
                "Use the returned file id, exact filename, and linked products unchanged. The host must obtain fresh " +
                "confirmation before every execution. Call this tool without assistant narration that says deletion " +
                "has started; it has not started while confirmation is pending. Only report deletion after this tool " +
                "returns ok: true, otherwise report the actionable error accurately.",
            parameters: {
                type: "object",
                properties: {
                    file_id: { type: "number", description: "Exact file id returned by the resolver." },
                    filename: { type: "string", description: "Exact filename returned by the resolver." },
                    linked_products: {
                        type: "array",
                        items: { type: "string" },
                        description: "Linked product names returned by the resolver, shown during confirmation."
                    }
                },
                required: ["file_id", "filename", "linked_products"],
                additionalProperties: false
            },
            execute: async ({ file_id, filename, linked_products }, ctx) => {
                const resolved = resolvedDeletionTargets.get(file_id);
                const linkedProductsMatch =
                    resolved?.linkedProducts.length === linked_products.length &&
                    resolved.linkedProducts.every((product, index) => product === linked_products[index]);
                if (!resolved || resolved.filename !== filename || !linkedProductsMatch) {
                    return {
                        ok: false,
                        reason: "unresolved_target",
                        error: "The file target is no longer verified. Resolve the exact file again before deleting it."
                    };
                }

                try {
                    const token = await getToken();
                    const response = await fetchWithAuth(
                        `${BACKEND_URI}/api/vector_files/delete/${file_id}`,
                        token,
                        { method: "DELETE" }
                    );
                    const payload = await response.json().catch(() => ({}));
                    if (!response.ok) {
                        const reason = response.status === 404
                            ? "not_found"
                            : response.status === 403
                              ? "unauthorized_path"
                              : "delete_failed";
                        return {
                            ok: false,
                            reason,
                            error: typeof payload?.error === "string"
                                ? payload.error
                                : "The file could not be deleted. Try again."
                        };
                    }

                    resolvedDeletionTargets.delete(file_id);
                    ctx.emit("uploaded_file_deleted", { fileId: file_id, filename: resolved.filename });
                    return {
                        ok: true,
                        fileId: file_id,
                        filename: resolved.filename,
                        message: payload?.message || `File '${resolved.filename}' deleted successfully.`
                    };
                } catch {
                    return {
                        ok: false,
                        reason: "delete_failed",
                        error: "The file could not be deleted. Check your connection and try again."
                    };
                }
            }
        }),
        { requiresConfirmation: true as const }
    );

    const navigate_to_previous_screen = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "navigate_to_previous_screen",
        category: "mutation",
        description:
            "Navigates to the previous app screen based on where the operator currently is. " +
            "Use this for generic requests like 'go back', 'go back to the previous screen', 'previous page', or 'return'. " +
            "Context behavior: editor -> Feature Studio, Feature Studio -> Products page.",
        parameters: {
            type: "object",
            properties: {},
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (_args, ctx) => {
            ctx.emit("navigate_to_previous_screen", {});
            return {
                ok: true,
                message: "Navigating to previous screen."
            };
        }
    });



    const generate_work_product = defineChatTool<GenerateWorkProductArgs, AppChatEventMap>({
        name: "generate_work_product",
        category: "mutation",
        description:
            "Triggers work product generation in the current Feature Studio context. " +
            "Use this for requests like 'generate PRD', 'generate user journeys', 'create risk assessment'. " +
            "If user intent names a different feature or user story, pass targetFeatureName/targetUserStoryName/targetUserStoryNumber to help backend scope resolution. " +
            "For an independently selected SDD Optional Work Product in the To Generate state, pass the exact elementId returned by list_ui_elements so the host activates that scoped card. " +
            "For the current hierarchy, provide workProductName when possible and optionally pass scope. " +
            "Backend resolver outcomes are authoritative when available. " +
            "If the result requires confirmation, ask its exact confirmationPrompt. On confirmation, call generate_work_product with the exact onConfirm arguments and confirmationToken without remapping names or scope. " +
            "Set forceNewVersion=true for explicit regenerate/new/rebuild requests so existing edited content is replaced with a fresh generated version.",
        parameters: {
            type: "object",
            properties: {
                elementId: {
                    type: "string",
                    description: "Exact scoped work-product element id returned by list_ui_elements. Use only for an SDD Optional Work Product in the To Generate state."
                },
                workProductId: { type: "string", description: "Optional work product ID when known." },
                workProductName: { type: "string", description: "Visible work product name, e.g., 'User Journeys'." },
                targetFeatureName: { type: "string", description: "Optional explicit feature name target when request references another feature." },
                targetUserStoryName: { type: "string", description: "Optional explicit user story name target when request references another story." },
                targetUserStoryNumber: { type: "string", description: "Optional explicit user story number/ID target (e.g., US-778)." },
                confirmationToken: {
                    type: "string",
                    description: "Backend-issued confirmation token returned by a prior confirmation_required result."
                },
                phase: {
                    type: "string",
                    description: "Optional SDLC phase hint: discovery|design|code|qa|deploy."
                },
                forceNewVersion: {
                    type: "boolean",
                    description: "When true, force generation of a new work-product version even if one already exists."
                },
                scope: {
                    type: "object",
                    description: "Explicit current hierarchy scope. Omit when elementId identifies an independently scoped SDD card.",
                    properties: {
                        level: { type: "string", enum: ["PRODUCT", "FEATURE", "USERSTORY"] },
                        featureId: { type: "number" },
                        userStoryId: { type: "string" }
                    },
                    required: ["level"],
                    additionalProperties: false
                }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const elementId = typeof args.elementId === "string" ? args.elementId.trim() : "";
            const requestedWorkProductId = typeof args.workProductId === "string" ? args.workProductId.trim() : "";
            let requestedWorkProductName = typeof args.workProductName === "string" ? args.workProductName.trim() : "";
            let targetFeatureName = typeof args.targetFeatureName === "string" ? args.targetFeatureName.trim() : "";
            let targetUserStoryName = typeof args.targetUserStoryName === "string" ? args.targetUserStoryName.trim() : "";
            const targetUserStoryNumber = typeof args.targetUserStoryNumber === "string" ? args.targetUserStoryNumber.trim() : "";
            const confirmationToken = typeof args.confirmationToken === "string" ? args.confirmationToken.trim() : "";
            const isConfirmationReplay = confirmationToken.length > 0;
            const currentWorkProduct = getCurrentWorkProductSnapshot();

            if (!isConfirmationReplay && requestedWorkProductName && (!targetFeatureName || !targetUserStoryName)) {
                const parsedScopedName = parseScopedWorkProductName(requestedWorkProductName);
                if (parsedScopedName) {
                    requestedWorkProductName = parsedScopedName.workProductName;
                    if (!targetFeatureName && parsedScopedName.targetFeatureName) {
                        targetFeatureName = parsedScopedName.targetFeatureName;
                    }
                    if (!targetUserStoryName && parsedScopedName.targetUserStoryName) {
                        targetUserStoryName = parsedScopedName.targetUserStoryName;
                    }
                }
            }

            let resolvedWorkProductId = requestedWorkProductId || undefined;
            let resolvedWorkProductName = requestedWorkProductName || undefined;

            if (!isConfirmationReplay && !elementId && !resolvedWorkProductId && !resolvedWorkProductName && currentWorkProduct?.workProductId) {
                resolvedWorkProductId = currentWorkProduct.workProductId;
                const currentTitle = currentWorkProduct.workProductTitle || "";
                if (currentTitle) {
                    resolvedWorkProductName = currentTitle;
                }
            }

            if (!isConfirmationReplay && !elementId && !resolvedWorkProductName && currentWorkProduct?.workProductTitle) {
                resolvedWorkProductName = currentWorkProduct.workProductTitle;
            }

            if (!elementId && !resolvedWorkProductId && !resolvedWorkProductName) {
                return {
                    error: "Provide elementId, workProductName, or workProductId."
                };
            }

            if (elementId && !elementId.startsWith("wp-")) {
                return { error: "elementId must be an exact work-product id returned by list_ui_elements." };
            }

            const currentHierarchy = getCurrentHierarchySnapshot();
            const inferredScope = currentHierarchy
                ? currentHierarchy.hierarchyLevel === "user_story"
                    ? {
                          level: "USERSTORY" as const,
                          featureId: normalizePositiveId(currentHierarchy.featureId),
                          userStoryId: currentHierarchy.userStoryId ? String(currentHierarchy.userStoryId) : null
                      }
                    : currentHierarchy.hierarchyLevel === "feature"
                      ? {
                            level: "FEATURE" as const,
                            featureId: normalizePositiveId(currentHierarchy.featureId),
                            userStoryId: null
                        }
                      : { level: "PRODUCT" as const, featureId: null, userStoryId: null }
                : undefined;
            // Models sometimes include the requested level but serialize unknown IDs as
            // `0`/`""`. When that level is the level already selected in Feature Studio,
            // use the authoritative UI snapshot for only those missing identifiers. Keep
            // concrete identifiers intact so a genuinely different scope is still rejected.
            const requestedLevelMatchesCurrent = args.scope?.level === inferredScope?.level;
            const requestedFeatureId = args.scope?.level === "PRODUCT"
                ? null
                : normalizePositiveId(args.scope?.featureId);
            const requestedUserStoryId =
                args.scope?.level === "USERSTORY" && args.scope.userStoryId
                    ? String(args.scope.userStoryId)
                    : null;
            const requestedScope = args.scope
                ? {
                      level: args.scope.level,
                      featureId:
                          args.scope.level === "PRODUCT"
                              ? null
                              : requestedFeatureId ??
                                (args.scope.level === "FEATURE"
                                    ? inferredScope?.featureId ?? null
                                    : requestedLevelMatchesCurrent
                                      ? inferredScope?.featureId ?? null
                                      : null),
                      userStoryId:
                          args.scope.level === "USERSTORY"
                              ? requestedUserStoryId ?? (requestedLevelMatchesCurrent ? inferredScope?.userStoryId ?? null : null)
                              : null
                  }
                : undefined;
            const exactReplayScope = args.scope
                ? {
                      level: args.scope.level,
                      featureId: args.scope.level === "PRODUCT" ? null : normalizePositiveId(args.scope.featureId),
                      userStoryId:
                          args.scope.level === "USERSTORY" && args.scope.userStoryId
                              ? String(args.scope.userStoryId)
                              : null
                  }
                : undefined;
            const scope = isConfirmationReplay ? exactReplayScope : requestedScope ?? inferredScope;

            const scopeAllowedFromCurrent =
                !requestedScope ||
                !inferredScope ||
                canUseRequestedScopeFromCurrent(inferredScope, requestedScope);
            if (!requestWorkProductGeneration && !isConfirmationReplay && !elementId && !scopeAllowedFromCurrent) {
                return {
                    error:
                        "The requested scope is lower than the current hierarchy or points outside the active context. Navigate there first, or pass the exact elementId of a visible independently scoped SDD card."
                };
            }

            if (!requestWorkProductGeneration && !isConfirmationReplay && !elementId && scope?.level === "FEATURE" && scope.featureId == null) {
                return { error: "featureId is required for FEATURE scope." };
            }
            if (!requestWorkProductGeneration && !isConfirmationReplay && !elementId && scope?.level === "USERSTORY" && (scope.featureId == null || !scope.userStoryId)) {
                return { error: "featureId and userStoryId are required for USERSTORY scope." };
            }

            const forceNewVersion = isConfirmationReplay ? args.forceNewVersion : (args.forceNewVersion ?? true);

            const request = {
                elementId: elementId || undefined,
                workProductId: resolvedWorkProductId,
                workProductName: resolvedWorkProductName,
                ...(targetFeatureName ? { targetFeatureName } : {}),
                ...(targetUserStoryName ? { targetUserStoryName } : {}),
                ...(targetUserStoryNumber ? { targetUserStoryNumber } : {}),
                ...(confirmationToken ? { confirmationToken } : {}),
                phase: args.phase,
                ...(typeof forceNewVersion === "boolean" ? { forceNewVersion } : {}),
                ...(scope ? { scope } : {})
            };

            if (requestWorkProductGeneration) {
                const result = await requestWorkProductGeneration(request);
                if (
                    !result.ok &&
                    result.confirmationRequired &&
                    Array.isArray(result.candidates) &&
                    result.candidates.length >= 2 &&
                    !result.confirmationPrompt
                ) {
                    return {
                        ...result,
                        confirmationPrompt: buildWorkProductCandidatePrompt(result.candidates)
                    } as WorkProductGenerationResult;
                }
                return result;
            }

            ctx.emit("generate_work_product", request);

            return {
                ok: true,
                message: `Generation request sent for ${resolvedWorkProductName || resolvedWorkProductId || elementId}.`
            };
        }
    });

    const start_product_refresh_session = defineChatTool<
        { projectId?: number; projectName?: string },
        AppChatEventMap
    >({
        name: "start_product_refresh_session",
        category: "mutation",
        description:
            "Starts a Product Refresh draft session with a mandatory phase-1 disclaimer. " +
            "Use this when the user asks to modify foundational product inputs.",
        parameters: {
            type: "object",
            properties: {
                projectId: { type: "number" },
                projectName: { type: "string" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args) => {
            const token = await getToken();
            const projects = await fetchProjects(token);
            const resolvedProject = resolveProjectTarget(projects, {
                projectId: args.projectId,
                projectName: args.projectName
            });
            if (!resolvedProject.target) {
                return {
                    error: resolvedProject.error,
                    ...(resolvedProject.matches ? { matches: resolvedProject.matches } : {})
                };
            }

            const response = await fetchWithAuth(`${BACKEND_URI}/api/product-refresh/sessions/start`, token, {
                method: "POST",
                body: JSON.stringify({ projectId: resolvedProject.target.project_id })
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                return { error: body?.error || "Failed to start Product Refresh session." };
            }

            const payload = await response.json();
            activeRefreshSessionId = Number(payload?.session_id ?? 0) || null;
            activeRefreshTarget = {
                projectId: resolvedProject.target.project_id,
                projectName: resolvedProject.target.project_name ?? null
            };
            return {
                ok: true,
                session_id: activeRefreshSessionId,
                status: payload?.status ?? "active",
                phase: payload?.phase ?? "phase_1",
                script: payload?.script,
                choices: payload?.choices ?? []
            };
        }
    });

    const apply_product_refresh_update = defineChatTool<
        {
            input: string;
            updates?: Record<string, string>;
            sessionId?: number;
            projectId?: number;
            projectName?: string;
        },
        AppChatEventMap
    >({
        name: "apply_product_refresh_update",
        category: "mutation",
        description:
            "Applies user-proposed Product Refresh changes into draft-only state. " +
            "No live product data is modified by this tool.",
        parameters: {
            type: "object",
            properties: {
                input: { type: "string", description: "The user's natural-language change request." },
                updates: {
                    type: "object",
                    description: "Optional explicit matrix updates by field key.",
                    additionalProperties: { type: "string" }
                },
                sessionId: { type: "number" },
                projectId: { type: "number", description: "Optional product id to resolve session when none is active." },
                projectName: { type: "string", description: "Optional product name to resolve session when none is active." }
            },
            required: ["input"] as string[],
            additionalProperties: false
        },
        execute: async (args) => {
            const token = await getToken();
            let sessionId = normalizePositiveId(args.sessionId) ?? activeRefreshSessionId;
            const explicitUpdates = args.updates ?? {};
            const hasExplicitUpdates = Object.keys(explicitUpdates).length > 0;

            if (!sessionId) {
                const projects = await fetchProjects(token);
                const resolvedProject = resolveProjectTarget(projects, {
                    projectId: args.projectId,
                    projectName: args.projectName
                });

                if (!resolvedProject.target) {
                    return {
                        error:
                            "Product is required before applying refresh changes. " +
                            (resolvedProject.error || "Call list_projects and ask the user which product they mean."),
                        pending_input: args.input,
                        ...(resolvedProject.matches ? { matches: resolvedProject.matches } : {})
                    };
                }

                const startResponse = await fetchWithAuth(`${BACKEND_URI}/api/product-refresh/sessions/start`, token, {
                    method: "POST",
                    body: JSON.stringify({ projectId: resolvedProject.target.project_id })
                });
                if (!startResponse.ok) {
                    const body = await startResponse.json().catch(() => ({}));
                    return {
                        error:
                            body?.error ||
                            "Failed to start Product Refresh session before applying your draft changes. Please retry.",
                        pending_input: args.input
                    };
                }

                const startPayload = await startResponse.json();
                sessionId = Number(startPayload?.session_id ?? 0) || null;
                activeRefreshSessionId = sessionId;
                activeRefreshTarget = {
                    projectId: resolvedProject.target.project_id,
                    projectName: resolvedProject.target.project_name ?? null
                };
            }

            if (!sessionId) {
                return {
                    error: "Unable to resolve a Product Refresh session. Please choose a product and retry.",
                    pending_input: args.input
                };
            }

            // Guardrail: question-style prompts should route through read-only draft fetch.
            if (!hasExplicitUpdates && isQuestionStyleRefreshInput(args.input)) {
                const draftResponse = await fetchWithAuth(
                    `${BACKEND_URI}/api/product-refresh/sessions/${sessionId}/draft`,
                    token,
                    { method: "GET" }
                );
                if (!draftResponse.ok) {
                    const body = await draftResponse.json().catch(() => ({}));
                    return { error: body?.error || "Failed to fetch Product Refresh draft state." };
                }
                const draftPayload = await draftResponse.json();
                return {
                    ok: true,
                    session_id: draftPayload?.session_id ?? sessionId,
                    phase: draftPayload?.phase,
                    intent_type: "read",
                    mutated: false,
                    changed_fields: draftPayload?.changed_fields ?? [],
                    impact: {},
                    update_modes: draftPayload?.update_modes ?? {},
                    field_change_notes: draftPayload?.field_change_notes ?? {},
                    delta_summary: draftPayload?.delta_summary ?? {},
                    draft: draftPayload?.draft ?? {},
                    baseline: draftPayload?.baseline ?? {},
                    requested_fields: []
                };
            }

            const response = await fetchWithAuth(`${BACKEND_URI}/api/product-refresh/sessions/${sessionId}/apply`, token, {
                method: "POST",
                body: JSON.stringify({ input: args.input, updates: explicitUpdates })
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                return {
                    error:
                        body?.error ||
                        "Failed to apply Product Refresh draft update. Please retry after confirming the target product.",
                    pending_input: args.input
                };
            }
            const payload = await response.json();
            return {
                ok: true,
                session_id: payload?.session_id ?? sessionId,
                phase: payload?.phase,
                intent_type: payload?.intent_type,
                mutated: payload?.mutated,
                changed_fields: payload?.changed_fields ?? [],
                update_modes: payload?.update_modes ?? {},
                field_change_notes: payload?.field_change_notes ?? {},
                impact: payload?.impact ?? {},
                delta_summary: payload?.delta_summary ?? {},
                draft: payload?.draft,
                baseline: payload?.baseline,
                requested_fields: payload?.requested_fields ?? []
            };
        }
    });

    const get_product_refresh_draft_state = defineChatTool<{ sessionId?: number }, AppChatEventMap>({
        name: "get_product_refresh_draft_state",
        category: "read",
        description:
            "Reads the current Product Refresh draft and baseline values without mutating anything. " +
            "Use this for question-style requests like 'what are current personas?'.",
        parameters: {
            type: "object",
            properties: {
                sessionId: { type: "number" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args) => {
            const sessionId = normalizePositiveId(args.sessionId) ?? activeRefreshSessionId;
            if (!sessionId) {
                return { error: "No active Product Refresh session. Call start_product_refresh_session first." };
            }
            const token = await getToken();
            const response = await fetchWithAuth(`${BACKEND_URI}/api/product-refresh/sessions/${sessionId}/draft`, token, {
                method: "GET"
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                return { error: body?.error || "Failed to fetch Product Refresh draft state." };
            }
            const payload = await response.json();
            return {
                ok: true,
                session_id: payload?.session_id ?? sessionId,
                status: payload?.status,
                phase: payload?.phase,
                draft: payload?.draft ?? {},
                baseline: payload?.baseline ?? {},
                changed_fields: payload?.changed_fields ?? [],
                update_modes: payload?.update_modes ?? {},
                field_change_notes: payload?.field_change_notes ?? {},
                delta_summary: payload?.delta_summary ?? {},
                mutated: false
            };
        }
    });

    const get_product_refresh_delta_summary = defineChatTool<{ sessionId?: number }, AppChatEventMap>({
        name: "get_product_refresh_delta_summary",
        category: "read",
        description:
            "Retrieves the phase-4 delta summary for the active Product Refresh draft session " +
            "before commit confirmation.",
        parameters: {
            type: "object",
            properties: {
                sessionId: { type: "number" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args) => {
            const sessionId = normalizePositiveId(args.sessionId) ?? activeRefreshSessionId;
            if (!sessionId) {
                return { error: "No active Product Refresh session. Call start_product_refresh_session first." };
            }
            const token = await getToken();
            const response = await fetchWithAuth(`${BACKEND_URI}/api/product-refresh/sessions/${sessionId}/delta`, token, {
                method: "GET"
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                return { error: body?.error || "Failed to fetch Product Refresh delta summary." };
            }
            const payload = await response.json();
            return {
                ok: true,
                session_id: payload?.session_id ?? sessionId,
                status: payload?.status,
                phase: payload?.phase,
                impact: payload?.impact ?? {},
                update_modes: payload?.update_modes ?? {},
                field_change_notes: payload?.field_change_notes ?? {},
                delta_summary: payload?.delta_summary ?? {}
            };
        }
    });

    const commit_product_refresh_session = defineChatTool<
        { user_confirmed: boolean; sessionId?: number },
        AppChatEventMap
    >({
        name: "commit_product_refresh_session",
        category: "mutation",
        description:
            "Commits Product Refresh draft data to live product data. " +
            "Requires explicit user_confirmed=true; otherwise commit is rejected.",
        parameters: {
            type: "object",
            properties: {
                user_confirmed: { type: "boolean" },
                sessionId: { type: "number" }
            },
            required: ["user_confirmed"] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const sessionId = normalizePositiveId(args.sessionId) ?? activeRefreshSessionId;
            if (!sessionId) {
                return {
                    error:
                        "No active Product Refresh session found. Resolve the target product first, then start_product_refresh_session."
                };
            }
            const token = await getToken();
            const response = await fetchWithAuth(`${BACKEND_URI}/api/product-refresh/sessions/${sessionId}/commit`, token, {
                method: "POST",
                body: JSON.stringify({ user_confirmed: args.user_confirmed })
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                return {
                    error:
                        body?.error ||
                        "Failed to commit Product Refresh session. Your draft is still active; retry commit after reviewing delta."
                };
            }
            const payload = await response.json();
            activeRefreshSessionId = null;
            const handoffProjectId = normalizePositiveId(payload?.handoff?.project_id);
            const handoffProjectName =
                typeof payload?.handoff?.project_name === "string" && payload.handoff.project_name.trim().length > 0
                    ? payload.handoff.project_name.trim()
                    : null;
            const targetProjectId = handoffProjectId ?? normalizePositiveId(activeRefreshTarget?.projectId);
            const targetProjectName =
                handoffProjectName ??
                (typeof activeRefreshTarget?.projectName === "string" && activeRefreshTarget.projectName.trim().length > 0
                    ? activeRefreshTarget.projectName.trim()
                    : null);
            const targetProductLabel =
                targetProjectName ?? (targetProjectId ? `product ${targetProjectId}` : "the target product");
            const currentProductContext = getCurrentProductSnapshot();
            activeRefreshTarget = null;
            // Always refresh Product Hub list after commit so users see updates immediately.
            ctx.emit("refresh_projects", {});
            if (targetProjectId) {
                const currentProjectId = normalizePositiveId(currentProductContext?.projectId);
                if (currentProjectId && currentProjectId === targetProjectId) {
                    // When already in the committed product context, refresh in-view work products/hierarchy.
                    ctx.emit("refresh_product_context", { projectId: targetProjectId });
                }
            }
            return {
                ok: true,
                session_id: payload?.session_id ?? sessionId,
                status: payload?.status ?? "committed",
                changed_fields: payload?.changed_fields ?? [],
                elapsed_ms: payload?.elapsed_ms,
                within_500ms_target: payload?.within_500ms_target,
                storage_verification: payload?.storage_verification ?? {},
                propagation: payload?.propagation ?? {},
                diagnostics: payload?.diagnostics ?? {},
                handoff: payload?.handoff ?? null,
                confirmation_message:
                    `Product Refresh changes were committed to ${targetProductLabel}. ` +
                    "To review the updates, open that product."
            };
        }
    });

    const abort_product_refresh_session = defineChatTool<{ sessionId?: number }, AppChatEventMap>({
        name: "abort_product_refresh_session",
        category: "mutation",
        description:
            "Aborts an active Product Refresh draft session and rolls view context back to the workspace.",
        parameters: {
            type: "object",
            properties: {
                sessionId: { type: "number" }
            },
            required: [] as string[],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            const sessionId = normalizePositiveId(args.sessionId) ?? activeRefreshSessionId;
            if (!sessionId) {
                return {
                    ok: true,
                    status: "aborted",
                    message: "No active Product Refresh session was found."
                };
            }
            const token = await getToken();
            const response = await fetchWithAuth(`${BACKEND_URI}/api/product-refresh/sessions/${sessionId}/abort`, token, {
                method: "POST"
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                return { error: body?.error || "Failed to abort Product Refresh session." };
            }
            const payload = await response.json();
            activeRefreshSessionId = null;
            activeRefreshTarget = null;
            ctx.emit("navigate_to_previous_screen", {});
            return {
                ok: true,
                session_id: payload?.session_id ?? sessionId,
                status: payload?.status ?? "aborted",
                restore: payload?.restore ?? null
            };
        }
    });

    return [
        get_current_product,
        get_new_product_draft,
        update_new_product_draft,
        get_context_mesh_draft,
        update_context_mesh_draft,
        update_primary_form_factor,
        get_current_hierarchy,
        get_current_work_product,
        list_projects,
        list_user_stories,
        list_features,
        navigate_to_project,
        navigate_to_feature,
        navigate_to_user_story,
        navigate_to_hierarchy_item,
        open_add_feature,
        open_add_user_story,
        generate_work_product,
        start_product_refresh_session,
        apply_product_refresh_update,
        get_product_refresh_draft_state,
        get_product_refresh_delta_summary,
        commit_product_refresh_session,
        abort_product_refresh_session,
        navigate_to_feature_studio,
        navigate_to_sdd_product_hierarchy,
        navigate_to_sdd_scope_picker,
        navigate_to_sdd_progress_report,
        navigate_to_sdd_stage,
        navigate_to_new_product,
        navigate_to_new_context_mesh,
        list_context_meshes,
        get_context_mesh_sources,
        navigate_to_context_mesh,
        navigate_to_context_mesh_hub,
        navigate_to_integrations,
        navigate_to_files,
        list_uploaded_files_with_products,
        assign_uploaded_file_to_product,
        resolve_uploaded_file_for_deletion,
        delete_uploaded_file,
        navigate_to_previous_screen,
        uploadFileToKnowledgeBaseTool,
        ...createJiraConnectTools(getToken),
        createIntegrationDisconnectTool(getToken, disconnectStateStore)
    ] as unknown as ChatTool[];
}
